  import React, {useState, useEffect, useRef, useLayoutEffect, createContext, useContext, useId, } from 'react';
  import { createPortal } from 'react-dom';
  import { HashRouter as Router, Routes, Route, Navigate, useNavigate, useLocation, } from 'react-router-dom';
  import './App.css';
  // import MaintenancePage from './MaintenancePage.js';
  import { Analytics } from "@vercel/analytics/react"
  import { SpeedInsights } from "@vercel/speed-insights/react"
  import gsap from 'gsap';
  import { ScrollTrigger } from 'gsap/ScrollTrigger';
  import Reveal from './Reveal';
  import GeometricBackground from './GeometricBackground';
  


  // function App() {
  //   return (
  //     <div className="App">
  //       <MaintenancePage />
  //       <h1 style={styles.heading}>🦅🦅🦅🦅🦅🦅🦅🦅🦅🦅
  //         Come back later lol
  //       </h1>
  //     </div>
  //   );
  // }

  // Scroll-to-section on load has to compete with every Reveal/ZoomOut/
  // ScrollHint on the homepage registering its own ScrollTrigger as it
  // mounts — a single requestAnimationFrame or two isn't reliably enough
  // time for that to settle, so this retries a few times over ~1s instead
  // of gambling on one attempt landing at the right moment.
  function deferredScrollTo(scrollFn, attemptsLeft = 4) {
    setTimeout(() => {
      scrollFn();
      if (attemptsLeft > 1) {
        deferredScrollTo(scrollFn, attemptsLeft - 1);
      }
    }, 300);
  }

  // Editorial/print palette for the whole site — warm paper instead of pure
  // black, ink instead of pure white, hairline rules instead of glowing
  // card borders. These point at CSS variables defined in index.css, so the
  // theme toggle (which flips data-theme on <html>) recolours every style.
  const PAPER = 'var(--paper)';
  const INK = 'var(--ink)';
  const INK_MUTED = 'var(--ink-muted)';
  const INK_FAINT = 'var(--ink-faint)';
  const INK_TINT = 'var(--ink-tint)';
  const HAIRLINE = 'var(--hairline)';
  const ICON_FILTER = 'var(--icon-filter)';

  // The address-bar colour on phones can't read CSS variables, so it's
  // set by hand to match --paper for each theme.
  const THEME_BAR_COLOR = { light: '#eee7d9', dark: '#000000' };

  // Brand icons don't recolour with a filter, so each theme gets its own
  // file: the -lightmode versions for paper, the originals for black.
  const NAV_ICONS = {
    light: { github: 'github-lightmode.svg', githubMobile: 'github-lightmode.svg', gmail: 'gmail-lightmode.svg', linkedin: 'linkedin-lightmode.svg' },
    dark: { github: 'github.svg', githubMobile: 'githubm.svg', gmail: 'gmail-lightmode.svg', linkedin: 'LLL.svg' },
  };

  const ThemeContext = createContext({ theme: 'light', toggleTheme: () => {} });

  function readSavedTheme() {
    try {
      return localStorage.getItem('theme') === 'dark' ? 'dark' : 'light';
    } catch (e) {
      return 'light';
    }
  }

  function useThemeState() {
    const [theme, setTheme] = useState(readSavedTheme);
    const transitionTimer = useRef(null);

    useEffect(() => {
      document.documentElement.dataset.theme = theme;
      const bar = document.querySelector('meta[name="theme-color"]');
      if (bar) bar.content = THEME_BAR_COLOR[theme];
      try {
        localStorage.setItem('theme', theme);
      } catch (e) {}
    }, [theme]);

    // Colours only animate while this class is on <html> (see index.css),
    // so the fade happens on a toggle but not on first load.
    const toggleTheme = () => {
      const root = document.documentElement;
      root.classList.add('theme-transition');
      window.clearTimeout(transitionTimer.current);
      transitionTimer.current = window.setTimeout(() => root.classList.remove('theme-transition'), 500);
      setTheme((t) => (t === 'dark' ? 'light' : 'dark'));
    };
    return { theme, toggleTheme };
  }

  // Fixed to the top-right corner on every page — each page's header keeps
  // that corner empty (the project pages' right-hand spacer, the homepage's
  // left-aligned nav).
  function ThemeToggle() {
    const { theme, toggleTheme } = useContext(ThemeContext);
    const [hover, setHover] = useState(false);
    const isDark = theme === 'dark';

    return (
      <button
        onClick={toggleTheme}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
        title={isDark ? 'Light mode' : 'Dark mode'}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        style={{
          ...styles.themeToggle,
          opacity: hover ? 0.6 : 1,
          transform: hover ? 'translateY(-2px) scale(1.05)' : 'translateY(0) scale(1)',
        }}
      >
        {isDark ? (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="4" />
            <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
          </svg>
        ) : (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
          </svg>
        )}
      </button>
    );
  }

  const PROJECT_SUMMARIES = {
    deepLearning: "Web app that automates Bosch's tool calibration tracking, alerts, and technician workload balancing.",
    trueframe: 'CLIP + FFT-fused model detecting AI-generated images, robust to compression and cropping — built in 72 hours for TikTok TechJam 2026.',
    onTrackSG: 'React Native app for real-time MRT/LRT timings, crowd levels, and service alerts across Singapore.',
    simpleReactFlow: 'Drag-and-drop flowchart builder with branching logic, built with React and ReactFlow.',
    planetEDA: 'ML-driven analysis of 572 exoplanets predicting Earth similarity using regression models.',
    loginReg: 'Full-stack auth boilerplate with JWT sessions, bcrypt hashing, and a Next.js/Express/MongoDB stack.',
    heartbleed: '3-layer interactive demo of CVE-2014-0160, from a toy exploit to a real attack on a simulated bank.',
    kaggleIMC: 'Machine learning-based 3D scene reconstruction pipeline combining DINOv2, SuperPoint/ALIKED, LightGlue, and COLMAP.',
    gitGram: 'Serverless bot that forwards GitHub pushes, PRs and issues to Telegram groups, built on Vercel and Upstash Redis.',
  };

  const SKILLS = [
    { category: 'Languages', items: ['Python', 'JavaScript', 'TypeScript', 'Java', 'C/C++', 'MATLAB', 'HTML', 'CSS', 'Tailwind CSS', 'Golang'] },
    { category: 'Frameworks & Libraries', items: ['React', 'React Native', 'Node.js', 'Express', 'Flask'] },
    { category: 'Databases', items: ['MySQL', 'PostgreSQL', 'MongoDB', 'Relational/Non-Relational Databases'] },
    { category: 'Tools & Platforms', items: ['Git', 'GitHub', 'Postman', 'Vercel', 'REST APIs', 'CI/CD', 'Docker', 'Firecracker'] },
    { category: 'Concepts', items: ['Data Structures & Algorithms', 'Object-Oriented Programming', 'Agile / Scrum', 'Serverless Computing'] },
    { category: 'Data Science & AI/ML', items: ['Exploratory Data Analysis (EDA)', 'Data Visualization', 'Machine Learning Basics', 'NumPy', 'Pandas', 'Matplotlib'] },
    { category: 'Hardware & Embedded', items: ['Embedded C', 'ARM Assembly', 'STM32', 'MSP432 Microcontroller', 'Linux'] },
    { category: 'Other Tools', items: ['Microsoft Office (Excel, PowerPoint, Word)', 'Cursor', 'Canva', 'Figma'] },
  ];

  // Best-effort logo lookup via devicon/simpleicons CDNs — deliberately not
  // exhaustive (pure concepts like "Agile / Scrum" have no real logo). Any
  // slug that 404s is hidden by the chip's onError handler, so a wrong/missing
  // mapping just degrades to a text-only chip instead of a broken-image icon.
  const SKILL_ICON_URLS = {
    'Python': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg',
    'JavaScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/javascript/javascript-original.svg',
    'TypeScript': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg',
    'Java': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg',
    'C/C++': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/cplusplus/cplusplus-original.svg',
    'MATLAB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/matlab/matlab-original.svg',
    'HTML': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/html5/html5-original.svg',
    'CSS': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/css3/css3-original.svg',
    'Tailwind CSS': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/tailwindcss/tailwindcss-original.svg',
    'Golang': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/go/go-original.svg',
    'React': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
    'React Native': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/react/react-original.svg',
    'Node.js': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg',
    'Express': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/express/express-original.svg',
    'Flask': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/flask/flask-original.svg',
    'MySQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mysql/mysql-original.svg',
    'PostgreSQL': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg',
    'MongoDB': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/mongodb/mongodb-original.svg',
    'Git': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/git/git-original.svg',
    'GitHub': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/github/github-original.svg',
    'Postman': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postman/postman-original.svg',
    'Vercel': 'https://cdn.simpleicons.org/vercel/000000',
    'Docker': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/docker/docker-original.svg',
    'NumPy': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/numpy/numpy-original.svg',
    'Pandas': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/pandas/pandas-original.svg',
    'Matplotlib': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/matplotlib/matplotlib-original.svg',
    'Embedded C': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/c/c-original.svg',
    'ARM Assembly': 'https://cdn.simpleicons.org/arm/000000',
    'STM32': 'https://cdn.simpleicons.org/stmicroelectronics/000000',
    'Linux': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linux/linux-original.svg',
    'Firecracker': `${process.env.PUBLIC_URL}/aws_firecracker.png`,
    'Microsoft Office (Excel, PowerPoint, Word)': `${process.env.PUBLIC_URL}/Microsoft_365_(2022).svg`,
    'Cursor': 'https://cdn.simpleicons.org/cursor/000000',
    'Canva': `${process.env.PUBLIC_URL}/canva-icon.svg`,
    'Figma': 'https://cdn.jsdelivr.net/gh/devicons/devicon/icons/figma/figma-original.svg',
  };

  // Scales the whole element up while fading it out as it scrolls past the
  // top of the viewport — used to make Skills feel like the camera is
  // "zooming into" it right as Projects rises into view underneath.
  function ZoomOut({ children, as: Tag = 'div', scaleTo = 1.5, style, sectionRef }) {
    const localRef = useRef(null);
    const ref = sectionRef || localRef;

    useLayoutEffect(() => {
      const el = ref.current;
      if (!el) return;

      // On short phone viewports, "top top" fires while the user has barely
      // scrolled into the section — push the start point later so there's
      // time to actually see the content before it starts zooming away.
      const isMobile = window.innerWidth <= 768;
      // A negative value here means the section's top has to scroll UP PAST
      // the viewport's top edge by that much before the effect starts (i.e.
      // later than "top top", not earlier) — a positive percent like "40%"
      // actually fires *earlier* since it's a point the section passes
      // through on its way up, before ever reaching the viewport's top.
      const start = isMobile ? 'top -60%' : 'top top';

      const ctx = gsap.context(() => {
        gsap.fromTo(
          el,
          { scale: 1, opacity: 1, transformOrigin: 'center center' },
          {
            scale: scaleTo,
            opacity: 0,
            ease: 'none',
            scrollTrigger: {
              trigger: el,
              start,
              end: 'bottom top',
              scrub: 0.3,
            },
          }
        );
      }, el);

      return () => ctx.revert();
    }, [scaleTo, ref]);

    return (
      <Tag ref={ref} style={style}>
        {children}
      </Tag>
    );
  }

  // A small bouncing "scroll down" arrow pinned to the bottom of a section,
  // that fades out over the first bit of scroll once you start leaving
  // that section (rather than lingering on top of the next one).
  function ScrollHint({ containerRef, onClick, style }) {
    const ref = useRef(null);

    useLayoutEffect(() => {
      const trigger = containerRef?.current;
      const el = ref.current;
      if (!trigger || !el) return;

      const ctx = gsap.context(() => {
        gsap.to(el, {
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger,
            start: 'top top',
            end: '+=250',
            scrub: true,
          },
        });
      }, el);

      return () => ctx.revert();
    }, [containerRef]);

    return (
      <button
        ref={ref}
        onClick={onClick}
        aria-label="Scroll down"
        className="scroll-hint"
        style={{ ...styles.scrollHint, ...style, cursor: onClick ? 'pointer' : 'default' }}
      >
        <span className="scroll-hint-arrow" style={styles.scrollHintArrow}>↓</span>
      </button>
    );
  }

  // A text nudge ("Scroll to view more") that sits at the top of a section
  // and fades out once you've started scrolling into it — unlike ScrollHint
  // (which sits at the bottom and hints at the *next* section), this hints
  // that the current section itself has more content below the fold.
  function ScrollMoreHint({ containerRef, label = 'Scroll to view more' }) {
    const ref = useRef(null);

    useLayoutEffect(() => {
      const trigger = containerRef?.current;
      const el = ref.current;
      if (!trigger || !el) return;

      const ctx = gsap.context(() => {
        gsap.to(el, {
          opacity: 0,
          ease: 'none',
          scrollTrigger: {
            trigger,
            start: 'top top',
            end: '+=200',
            scrub: true,
          },
        });
      }, el);

      return () => ctx.revert();
    }, [containerRef]);

    return (
      <div ref={ref} style={styles.scrollMoreHint}>
        {label} <span className="scroll-hint-arrow" style={styles.scrollMoreHintArrow}>↓</span>
      </div>
    );
  }

  function SkillChip({ item }) {
    const iconUrl = SKILL_ICON_URLS[item];
    const [hover, setHover] = useState(false);

    return (
      <span
        className="skill-chip"
        style={{
          ...styles.skillChip,
          borderColor: hover ? INK : 'var(--chip-border)',
        }}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        {iconUrl && (
          <span style={styles.skillChipIconWrap}>
            <img
              src={iconUrl}
              alt=""
              style={styles.skillChipIcon}
              onError={(e) => { e.currentTarget.parentElement.style.display = 'none'; }}
            />
          </span>
        )}
        {item}
      </span>
    );
  }

  function SkillsSection() {
    return (
      <Reveal
        as="div"
        style={styles.skillsGrid}
        stagger={0.35}
        y={16}
        rotateX={-75}
        transformOrigin="top center"
        perspective={900}
        duration={1}
        ease="none"
        start="top 95%"
        end="bottom bottom"
        scrub={0.15}
      >
        {SKILLS.map((group) => (
          <div key={group.category} style={styles.skillCard}>
            <h3 style={styles.skillCategoryTitle}>{group.category}</h3>
            <div style={styles.skillChipRow}>
              {group.items.map((item) => (
                <SkillChip key={item} item={item} />
              ))}
            </div>
          </div>
        ))}
      </Reveal>
    );
  }

  //Main component
  function App() {
  

    const navigate = useNavigate();
    const { theme } = useContext(ThemeContext);
    const navIcons = NAV_ICONS[theme];
    //const [hoverLinks, setHoverLinks] = useState(false);
    const [hoverResume, setHoverResume] = useState(false);
    const [hoverProjects, setHoverProjects] = useState(false);
    //const [hoverOthers, setHoverOthers] = useState(false);
    //const [hoverContact, setHoverContact] = useState(false);
    const [hoverGitHub, setHoverGitHub] = useState(false);
    const [hoverLinkedIn, setHoverLinkedIn] = useState(false);
    const [hoverGmail, setHoverGmail] = useState(false);
    const [hoverTelegram, setHoverTelegram] = useState(false);
    const [hoverOutlook, setHoverOutlook] = useState(false);

    //const [hoverLinksMobile, setHoverLinksMobile] = useState(false);
    const [hoverResumeMobile, setHoverResumeMobile] = useState(false);
    const [hoverProjectsMobile, setHoverProjectsMobile] = useState(false);
    //const [hoverOthersMobile, setHoverOthersMobile] = useState(false);
    //const [hoverContactMeMobile, setHoverContactMeMobile] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [hoverGitHubmobile, setHoverGitHubmobile] = useState(false);
    const [hoverLinkedInmobile, setHoverLinkedInmobile] = useState(false);
    const [hoverGmailmobile, setHoverGmailmobile] = useState(false);
    const [hoverTelegrammobile, setHoverTelegrammobile] = useState(false);
    const [hoverOutlookmobile, setHoverOutlookmobile] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [typed, setTyped] = useState('');
    const projectsSectionRef = useRef(null);
    const heroSectionRef = useRef(null);
    const skillsSectionRef = useRef(null);
    const location = useLocation();

    const scrollToProjects = () => projectsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });

    // Project detail pages' back button sends us here with this flag so
    // "back" lands you where you actually came from (the homepage's
    // Projects section) instead of just the top of the page.
    useEffect(() => {
      if (location.state?.scrollTo === 'projects') {
        deferredScrollTo(scrollToProjects);
      }
    }, [location.state]);

    // Shareable deep links — e.g. arunk.org/?section=projects opens the
    // homepage already scrolled to that section, so "here's all my
    // projects" can be one link instead of "go to my site, then scroll".
    // Read straight from window.location rather than react-router's
    // location.search: this is a HashRouter, so it only parses the ?query
    // when it's embedded inside the #hash (e.g. #/?section=projects) — a
    // query string sitting on the real URL outside the hash is invisible
    // to it, and that's the more natural way people actually type/share
    // this link, so check both places.
    useEffect(() => {
      const hashQueryIndex = window.location.hash.indexOf('?');
      const hashSearch = hashQueryIndex >= 0 ? window.location.hash.slice(hashQueryIndex) : '';
      const section = new URLSearchParams(window.location.search || hashSearch).get('section');
      const targets = { projects: scrollToProjects, skills: () => skillsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }) };
      if (targets[section]) {
        deferredScrollTo(targets[section]);
      }
    }, [location.pathname, location.key]);

    useEffect(() => {
      const full = "Hey! I'm Arun👋";
      const chars = Array.from(full);
      let i = 0;
      const timer = setInterval(() => {
        i++;
        setTyped(chars.slice(0, i).join(''));
        if (i >= chars.length) clearInterval(timer);
      }, 80);
      return () => clearInterval(timer);
    }, []);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (

      
      <div className="fade-in" style={styles.diffhomeContainer}>
      <div style={styles.heroSection} ref={heroSectionRef}>
        {theme === 'dark' && <GeometricBackground />}

        {/* <video
          autoPlay
          loop
          muted
          playsInline
          style={styles.videoBackground}
        >
          <source src={`${process.env.PUBLIC_URL}/wp.mp4`} type="video/mp4" />
          Your browser does not support the video tag.
        </video> */}
        <nav style={styles.navbar}>
          {/* Hamburger Menu Buttononly for mobile */}
          {isMobile && (
            
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              style={styles.hamburgerButton}
            >
              <div style={styles.hamburgerIcon}>
                <span style={{
                  ...styles.hamburgerLine, 
                  transform: isMobileMenuOpen ? 'rotate(45deg) translate(5px, 5px)' : 'none'
                }}></span>
                <span style={{
                  ...styles.hamburgerLine, 
                  opacity: isMobileMenuOpen ? 0 : 1
                }}></span>
                <span style={{
                  ...styles.hamburgerLine, 
                  transform: isMobileMenuOpen ? 'rotate(-45deg) translate(5px, -5px)' : 'none'
                }}></span>
              </div>
            </button>
          )}

          {/* Desktop Navigation - Only show on desktop */}
          {!isMobile && (

            <div className="fade-in" style={styles.navButtons}>
              {/* <img
            src={`${process.env.PUBLIC_URL}/BirdW.svg`}
            alt="Go back"
            style={styles.logoIcon}
          /> */}
                {/* <button
                  onClick={() => navigate('/Links')}
                  style={{
                    ...styles.LinkButton,
                    opacity: hoverLinks ? 0.6 : 1,
                    transform: hoverLinks ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={() => setHoverLinks(true)}
                  onMouseLeave={() => setHoverLinks(false)}
                >
                  View my links
                </button> */}


              <button
                onClick={() => navigate('/Resume')}
                style={{
                  ...styles.ResumeButton,
                  opacity: hoverResume ? 0.6 : 1,
                  transform: hoverResume ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverResume(true)}
                onMouseLeave={() => setHoverResume(false)}
              >
                View my Resume
              </button>
              <button
                onClick={scrollToProjects}
                style={{
                  ...styles.ProjectsButton,
                  opacity: hoverProjects ? 0.6 : 1,
                  transform: hoverProjects ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverProjects(true)}
                onMouseLeave={() => setHoverProjects(false)}
              >
                View my projects
              </button>

              {/* <button
                onClick={() => navigate('/Others')}
                style={{
                  ...styles.OthersButton,
                  opacity: hoverOthers ? 0.6 : 1,
                  transform: hoverOthers ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverOthers(true)}
                onMouseLeave={() => setHoverOthers(false)}
              >
                Others
              </button> */}


              {/* <button
                onClick={() => navigate('/ContactMe')}
                style={{
                  ...styles.ContactmeButton,
                  opacity: hoverContact ? 0.6 : 1,
                  transform: hoverContact ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverContact(true)}
                onMouseLeave={() => setHoverContact(false)}
              >
                Contact Me
              </button> */}

                                      <button
                onClick={() => window.open('https://github.com/ArunKarthick19')}
                style={{
                  ...styles.githubnew,
                  opacity: hoverGitHub ? 0.6 : 1,
                  transform: hoverGitHub ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverGitHub(true)}
                onMouseLeave={() => setHoverGitHub(false)}
              >
                <img
            src={`${process.env.PUBLIC_URL}/${navIcons.github}`}
            alt="Github"
            style={styles.Noinvericon}
          />
              </button>

                            <button
                onClick={() => window.open('mailto:arunkarthick19@gmail.com')}
                style={{
                  ...styles.gmailnew,
                  opacity: hoverGmail ? 0.6 : 1,
                  transform: hoverGmail ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverGmail(true)}
                onMouseLeave={() => setHoverGmail(false)}
              >
                <img
            src={`${process.env.PUBLIC_URL}/${navIcons.gmail}`}
            alt="Gmail"
            style={styles.Noinvericon}
          />
              </button>

                  <button
                onClick={() => window.open('https://www.linkedin.com/in/s-arun-karthick-48b35a25a/')}
                style={{
                  ...styles.LinkedinNew,
                  opacity: hoverLinkedIn ? 0.6 : 1,
                  transform: hoverLinkedIn ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverLinkedIn(true)}
                onMouseLeave={() => setHoverLinkedIn(false)}
              >
                <img
            src={`${process.env.PUBLIC_URL}/${navIcons.linkedin}`}
            alt="LinkedIn"
            style={styles.Noinvericon}
          />
              </button>

      

        

                                          <button
                onClick={() => window.open('https://t.me/arunk1903')}
                style={{
                  ...styles.telenew,
                  opacity: hoverTelegram ? 0.6 : 1,
                  transform: hoverTelegram ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverTelegram(true)}
                onMouseLeave={() => setHoverTelegram(false)}
              >
                <img
            src={`${process.env.PUBLIC_URL}/tele.svg`}
            alt="Tele"
            style={styles.Noinvericon}
          />
              </button>
                                                  <button
                onClick={() => window.open('mailto:ARUNKART001@e.ntu.edu.sg')}
                style={{
                  ...styles.outlook,
                  opacity: hoverOutlook ? 0.6 : 1,
                  transform: hoverOutlook ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverOutlook(true)}
                onMouseLeave={() => setHoverOutlook(false)}
              >
                <img
            src={`${process.env.PUBLIC_URL}/outlook.svg`}
            alt="Outlook"
            style={styles.Noinvericon}
          />
              </button>


    
          
            </div>
          
          )}

          

          {/* Mobile Menu - Only show on mobile when open */}
          {isMobile && (
            <div style={{
              ...styles.mobileMenu,
              transform: isMobileMenuOpen ? 'translateX(0)' : 'translateX(-100%)'
            }}>
              
              <button
                onClick={() => {
                  navigate('/Resume');
                  setIsMobileMenuOpen(false);
                }}
              style={{
                  ...styles.MobileButton,
                  opacity: hoverResumeMobile ? 0.6 : 1,
                  transform: hoverResumeMobile ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverResumeMobile(true)}
                onMouseLeave={() => setHoverResumeMobile(false)}
              >
                View my Resume
              </button>
              <button
                onClick={() => {
                  scrollToProjects();
                  setIsMobileMenuOpen(false);
                }}
                style={{
                  ...styles.MobileButton,
                  opacity: hoverProjectsMobile ? 0.6 : 1,
                  transform: hoverProjectsMobile ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
                  transition: 'all 0.3s ease',
                }}
                onMouseEnter={() => setHoverProjectsMobile(true)}
                onMouseLeave={() => setHoverProjectsMobile(false)}
              >
                View my projects
              </button>



              {/* Social Media Icons Row */}
  <div style={styles.mobileSocialRow}>
    <button
      onClick={() => window.open('https://github.com/ArunKarthick19')}
      style={{
        ...styles.mobileIconButton,
        ...(hoverGitHubmobile ? styles.mobileIconButtonHover : {}),
      }}
      onMouseEnter={() => setHoverGitHubmobile(true)}
      onMouseLeave={() => setHoverGitHubmobile(false)}
    >
      <img
        src={`${process.env.PUBLIC_URL}/${navIcons.githubMobile}`}
        alt="Github"
        style={styles.mobileSocialIcon}
      />
    </button>

    <button
      onClick={() => window.open('mailto:arunkarthick19@gmail.com')}
      style={{
        ...styles.mobileIconButton,
        ...(hoverGmailmobile ? styles.mobileIconButtonHover : {}),
      }}
      onMouseEnter={() => setHoverGmailmobile(true)}
      onMouseLeave={() => setHoverGmailmobile(false)}
    >
      <img
        src={`${process.env.PUBLIC_URL}/${navIcons.gmail}`}
        alt="Gmail"
        style={styles.mobileSocialIcon}
      />
    </button>

    <button
      onClick={() => window.open('https://www.linkedin.com/in/s-arun-karthick-48b35a25a/')}
      style={{
        ...styles.mobileIconButton,
        ...(hoverLinkedInmobile ? styles.mobileIconButtonHover : {}),
      }}
      onMouseEnter={() => setHoverLinkedInmobile(true)}
      onMouseLeave={() => setHoverLinkedInmobile(false)}
    >
      <img
        src={`${process.env.PUBLIC_URL}/${navIcons.linkedin}`}
        alt="LinkedIn"
        style={styles.mobileSocialIcon}
      />
    </button>

    <button
      onClick={() => window.open('https://t.me/arunk1903')}
      style={{
        ...styles.mobileIconButton,
        ...(hoverTelegrammobile ? styles.mobileIconButtonHover : {}),
      }}
      onMouseEnter={() => setHoverTelegrammobile(true)}
      onMouseLeave={() => setHoverTelegrammobile(false)}
    >
      <img
        src={`${process.env.PUBLIC_URL}/tele.svg`}
        alt="Telegram"
        style={styles.mobileSocialIcon}
      />
    </button>

    <button
      onClick={() => window.open('mailto:ARUNKART001@e.ntu.edu.sg')}
      style={{
        ...styles.mobileIconButton,
        ...(hoverOutlookmobile ? styles.mobileIconButtonHover : {}),
      }}
      onMouseEnter={() => setHoverOutlookmobile(true)}
      onMouseLeave={() => setHoverOutlookmobile(false)}
    >
      <img
        src={`${process.env.PUBLIC_URL}/outlook.svg`}
        alt="Outlook"
        style={styles.mobileSocialIcon}
      />
    </button>
  </div>


            </div>
          )}
        </nav>
      
        {/* <h1 style={styles.heading}>Still under construction 😊</h1>
        <h2 style={styles.sheading}>But feel free to explore. Cheers!💫</h2>
        <h2 style={styles.sheading}>Best experienced on a desktop/laptop screen</h2> */}
        {/* <h1 style={styles.heading}>Hello! Welcome to my website</h1>
        <h2 style={styles.sheading}>Best experienced on a desktop/laptop screen💫💫</h2>
        <p style={styles.imageCaption}>Summit captured by me at Mt. Rinjani(3726m), Indonesia — July 2025</p> */}

          <Reveal as="div" style={styles.hero} stagger={0.15} y={24} start="top 100%">
    <h1 style={styles.heading}>{typed}<span className="typewriter-cursor">|</span></h1>

    <h2
    style={{
      ...styles.sheading,
      ...(isMobile ? { paddingLeft: '35px' , fontSize: '13px'} : {})
    }}
  >
    Computer Engineering undergrad at Nanyang Technological University, Singapore 💻🚀🏎️😀🛫
  </h2>
    {theme === 'dark' && (
    <p style={{ color: INK, fontSize: '0.78rem', marginTop: '12px', letterSpacing: '0.03em' }}>
      Click, drag and play around lol!(So Arun gave u access to this ahh 👀) ✨
    </p>
    )}
    <p style={{ color: INK, fontSize: '0.78rem', marginTop: '12px', letterSpacing: '0.03em' }}>
  Welcome to my backstage. This is where I test, experiment, play around and stage my pushes before they go live on my main website. So, if you see something broken, it's probably cuzz I was testing something. 😅
</p>
  {/* <h2
    style={{
      ...styles.sheading,
      ...(isMobile ? { paddingLeft: '30px' } : {})
    }}
  >
    Passionate about turning ideas into real-world technology solutions and creating a posetive impact
  </h2> */}


  </Reveal>
  {/* <p style={styles.imageCaption}>
      Summit captured by me at Mt. Rinjani(3726m), Indonesia — July 2025
    </p> */}

      <ScrollHint
        containerRef={heroSectionRef}
        onClick={() => {
          const el = skillsSectionRef.current;
          if (!el) return;
          // +120 scrolls a bit past the section's top edge instead of
          // stopping exactly at it — tweak this number to land wherever
          // feels right.
          const top = el.getBoundingClientRect().top + window.scrollY + 10;
          window.scrollTo({ top, behavior: 'smooth' });
        }}
      />

      </div>

      <ZoomOut as="section" style={styles.homeSection} scaleTo={1.4} sectionRef={skillsSectionRef}>
        <Reveal as="h2" style={styles.homeSectionTitle}>Skills</Reveal>
        <SkillsSection />
        <ScrollHint containerRef={skillsSectionRef} onClick={scrollToProjects} />
      </ZoomOut>

      <section ref={projectsSectionRef} style={{ ...styles.homeSection, paddingBottom: '250px' }}>
        <Reveal as="h2" style={styles.homeSectionTitle}>Projects</Reveal>
        <Reveal as="div" style={styles.projectsGitHubLinkWrap} delay={0.15}>
          <a
            href="https://github.com/ArunKarthick19/Projects"
            target="_blank"
            rel="noreferrer"
            className="projects-github-link"
            style={styles.projectsGitHubLink}
          >
            <img
              src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
              alt=""
              style={{ ...styles.GitIcon_Projects, filter: ICON_FILTER }}
            />
            View in GitHub
          </a>
        </Reveal>
        <ScrollMoreHint containerRef={projectsSectionRef} />
        <ProjectList style={{ marginTop: '20px' }} />
      </section>

      </div>
    );
  }

  // The resume viewer itself, shared between the standalone /resume page
  // and the inline "Resume" section on the homepage.
  function ResumeViewer() {
    const [hoverDownload, setHoverDownload] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      checkMobile();
      window.addEventListener('resize', checkMobile);

      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
      <>
        <Reveal
          as="button"
          y={30}
          onClick={() => {
            const link = document.createElement('a');
            link.href = '/Arun_Resume.pdf';
            link.download = 'Arun_Resume.pdf';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          }}
          style={{
            ...styles.downloadButton,
            ...(isMobile ? styles.downloadButtonMobile : {}),
            opacity: hoverDownload ? 0.6 : 1,
            transform: hoverDownload ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverDownload(true)}
          onMouseLeave={() => setHoverDownload(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/download.svg`}
            alt="Download"
            style={styles.downloadIcon}
          />
          Download my Resume
        </Reveal>
        <Reveal as="div" y={30} delay={0.15} style={styles.pdfContainer}>
          <iframe
            src={`${process.env.PUBLIC_URL}/Arun_Resume.pdf`}
            title="Resume"
            style={styles.pdfViewer}
          />
        </Reveal>
      </>
    );
  }

  // Standalone /resume page: back button + the shared ResumeViewer.
  function Resume() {
    const navigate = useNavigate();
    const [hoverBackResume, setHoverBackResume] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      checkMobile();
      window.addEventListener('resize', checkMobile);

      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
      <div className="fade-in" style={styles.resumeContainer}>
        <button
          onClick={() => navigate('/')}
          style={{
            ...styles.GoBack,
            ...(isMobile ? styles.GoBackMobile : {}),
            opacity: hoverBackResume ? 0.6 : 1,
            transform: hoverBackResume ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverBackResume(true)}
          onMouseLeave={() => setHoverBackResume(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/return.svg`}
            alt="Go back"
            style={styles.GoBackIcon}
          />
        </button>
        <ResumeViewer />
      </div>
    )
  }

  function Links() {
    const navigate = useNavigate();
    const [hoverLinkedIn, setHoverLinkedIn] = useState(false);
    const [hoverGit, setHoverGit] = useState(false);
    const [hoverBack, setHoverBack] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
      <div className="fade-in link-container" style={{
        ...styles.LinkPage,
        ...(isMobile ? styles.LinkPageMobile : {})
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            ...styles.GoBack,
            ...(isMobile ? styles.GoBackMobile : {}),
            opacity: hoverBack ? 0.6 : 1,
            transform: hoverBack ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverBack(true)}
          onMouseLeave={() => setHoverBack(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/return.svg`}
            alt="Go back"
            style={styles.GoBackIcon} 
          />
        </button>
        <Reveal
          as="button"
          y={30}
          onClick={() => window.open('https://www.linkedin.com/in/s-arun-karthick-48b35a25a/')}
          style={{
            ...styles.linkedin,
            opacity: hoverLinkedIn ? 0.6 : 1,
            transform: hoverLinkedIn ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverLinkedIn(true)}
          onMouseLeave={() => setHoverLinkedIn(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/linkedin-svgrepo-com.svg`}
            alt="LinkedIn"
            style={styles.linkedinIcon}
          />
        </Reveal>
        <Reveal
          as="button"
          y={30}
          delay={0.12}
          onClick={() => window.open('https://github.com/ArunKarthick19')}
          style={{
            ...styles.git,
            opacity: hoverGit ? 0.6 : 1,
            transform: hoverGit ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverGit(true)}
          onMouseLeave={() => setHoverGit(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
            alt="Github"
            style={styles.GitIcon}
          />
          GitHub
        </Reveal>
      </div>
    );
  }

  // The GSAP scroll-fold reveal (in ProjectList) animates the outer <div>
  // below, never the <button> itself — otherwise GSAP's per-scroll-frame
  // writes to `transform` would permanently strip the button's own CSS
  // hover transition (React never re-touches a style property whose value
  // hasn't changed since last render, so a GSAP override sticks forever).
  function ProjectCard({ onClick, title, description, hover, onMouseEnter, onMouseLeave }) {
    return (
      <button
        onClick={onClick}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={{
          ...styles.projectCard,
          backgroundColor: hover ? INK_TINT : 'transparent',
        }}
      >
        <span style={styles.projectCardTop}>
          <span style={{ ...styles.projectCardTitle, borderBottomColor: hover ? INK : 'transparent' }}>{title}</span>
        </span>
        {description && <span style={styles.projectCardDescription}>{description}</span>}
        <span style={styles.projectCardCta}>
          View project
          <span style={{ ...styles.projectCardArrow, transform: hover ? 'translateX(4px)' : 'translateX(0)' }}>→</span>
        </span>
      </button>
    );
  }

  // Auto-rotates through a mix of PDF and image slides, pausing while the
  // viewer's cursor is over it so it doesn't yank a PDF away mid-read.
  function ProjectCarousel({ slides, interval = 4000 }) {
    const [index, setIndex] = useState(0);
    const [paused, setPaused] = useState(false);
    const [hoverPrev, setHoverPrev] = useState(false);
    const [hoverNext, setHoverNext] = useState(false);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const [hoverLightboxClose, setHoverLightboxClose] = useState(false);

    useEffect(() => {
      if (paused || slides.length <= 1) return;
      const timer = setInterval(() => {
        setIndex((i) => (i + 1) % slides.length);
      }, interval);
      return () => clearInterval(timer);
    }, [paused, slides.length, interval]);

    useEffect(() => {
      if (!lightboxOpen) return;
      const onKeyDown = (e) => {
        if (e.key === 'Escape') setLightboxOpen(false);
      };
      window.addEventListener('keydown', onKeyDown);
      return () => window.removeEventListener('keydown', onKeyDown);
    }, [lightboxOpen]);

    // The carousel's images/PDF load in async, after the parent <Reveal>'s
    // ScrollTrigger has already calculated its start/end pixel positions.
    // If loading shifts the page's layout, that calculation goes stale and
    // the reveal can fire at the wrong scroll position (or never). Refresh
    // ScrollTrigger once everything here has actually loaded.
    useEffect(() => {
      const imageSlides = slides.filter((s) => s.type === 'image');
      if (imageSlides.length === 0) return;

      let cancelled = false;
      let loadedCount = 0;

      const handleLoad = () => {
        loadedCount++;
        if (!cancelled && loadedCount === imageSlides.length) {
          requestAnimationFrame(() => ScrollTrigger.refresh());
        }
      };

      const imgEls = imageSlides.map((s) => {
        const img = new Image();
        img.onload = handleLoad;
        img.onerror = handleLoad;
        img.src = s.src;
        return img;
      });

      return () => {
        cancelled = true;
        imgEls.forEach((img) => {
          img.onload = null;
          img.onerror = null;
        });
      };
    }, [slides]);

    const goTo = (delta) => {
      setIndex((i) => (i + delta + slides.length) % slides.length);
    };

    const currentSlide = slides[index];

    return (
      <>
      <div
        style={styles.carousel}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
      >
        <div style={styles.carouselRow}>
          {slides.length > 1 && (
            <button
              aria-label="Previous slide"
              onClick={() => goTo(-1)}
              onMouseEnter={() => setHoverPrev(true)}
              onMouseLeave={() => setHoverPrev(false)}
              style={{
                ...styles.carouselNavBtn,
                backgroundColor: hoverPrev ? INK_TINT : "transparent",
                transform: hoverPrev ? "translateX(-3px) scale(1.08)" : "translateX(0) scale(1)",
              }}
            >
              ‹
            </button>
          )}

          <div style={styles.carouselStage}>
            {slides.map((slide, i) => (
              <div
                key={i}
                style={{
                  ...styles.carouselSlide,
                  opacity: i === index ? 1 : 0,
                  pointerEvents: i === index ? 'auto' : 'none',
                }}
              >
                {slide.type === 'pdf' ? (
                  <iframe src={slide.src} title={slide.title || `slide-${i}`} style={styles.carouselPdf} />
                ) : (
                  <img
                    src={slide.src}
                    alt={slide.alt || ''}
                    title="Double-click to view full size"
                    onDoubleClick={() => setLightboxOpen(true)}
                    style={{ ...styles.carouselImage, cursor: 'zoom-in' }}
                  />
                )}
              </div>
            ))}
          </div>

          {slides.length > 1 && (
            <button
              aria-label="Next slide"
              onClick={() => goTo(1)}
              onMouseEnter={() => setHoverNext(true)}
              onMouseLeave={() => setHoverNext(false)}
              style={{
                ...styles.carouselNavBtn,
                backgroundColor: hoverNext ? INK_TINT : "transparent",
                transform: hoverNext ? "translateX(3px) scale(1.08)" : "translateX(0) scale(1)",
              }}
            >
              ›
            </button>
          )}
        </div>
        <div style={styles.carouselDots}>
          {slides.map((_, i) => (
            <button
              key={i}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
              style={{
                ...styles.carouselDot,
                backgroundColor: i === index ? INK : HAIRLINE,
              }}
            />
          ))}
        </div>
      </div>

      {lightboxOpen && currentSlide && currentSlide.type === 'image' && createPortal(
        <div
          className="fade-in"
          style={styles.lightboxOverlay}
          onClick={() => setLightboxOpen(false)}
        >
          <button
            aria-label="Close full size view"
            onClick={() => setLightboxOpen(false)}
            onMouseEnter={() => setHoverLightboxClose(true)}
            onMouseLeave={() => setHoverLightboxClose(false)}
            style={{
              ...styles.lightboxCloseBtn,
              backgroundColor: hoverLightboxClose ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)",
              transform: hoverLightboxClose ? "scale(1.1)" : "scale(1)",
            }}
          >
            ×
          </button>
          <img
            src={currentSlide.src}
            alt={currentSlide.alt || ''}
            style={styles.lightboxImage}
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
      </>
    );
  }

  function ProjectList({ style }) {
      const navigate = useNavigate();
      const [hoverDeepLearning, setHoverDeepLearning] = useState(false);
      const [hoverOnTrackSG, setHoverOnTrackSG] = useState(false);
      const [hoverSimpleReactFlowProject, setHoverSimpleReactFlowProject] = useState(false);
      const [hoverPlanetEDA, setHoverPlanetEDA] = useState(false);
      const [hoverLoginRegTemplate, setHoverLoginRegTemplate] = useState(false);
      const [hoverHeartbleedDemo, setHoverHeartbleedDemo] = useState(false);
      const [hoverKaggleIMC, setHoverKaggleIMC] = useState(false);
      const [hoverTrueFrame, setHoverTrueFrame] = useState(false);
      const [hoverGitGram, setHoverGitGram] = useState(false);

    return(
      <Reveal
        as="div"
        style={{ ...styles.projectListStack, ...style }}
        stagger={0.5}
        y={16}
        rotateX={-75}
        transformOrigin="top center"
        perspective={900}
        duration={1}
        ease="none"
        start="top 95%"
        end="bottom bottom"
        scrub={0.15}
      >

      <ProjectCard
        onClick={() => navigate("/DeepLearning")}
        title="DeepLearning MLDA@EEE 2025 Hackathon"
        description={PROJECT_SUMMARIES.deepLearning}
        hover={hoverDeepLearning}
        onMouseEnter={() => setHoverDeepLearning(true)}
        onMouseLeave={() => setHoverDeepLearning(false)}
      />

      <ProjectCard
        onClick={() => navigate("/TrueFrame")}
        title="TrueFrame — Robust Detection of AI-Generated Content"
        description={PROJECT_SUMMARIES.trueframe}
        hover={hoverTrueFrame}
        onMouseEnter={() => setHoverTrueFrame(true)}
        onMouseLeave={() => setHoverTrueFrame(false)}
      />

      <ProjectCard
        onClick={() => navigate("/KaggleIMC")}
        title="Kaggle Image Matching Challenge (IMC)"
        description={PROJECT_SUMMARIES.kaggleIMC}
        hover={hoverKaggleIMC}
        onMouseEnter={() => setHoverKaggleIMC(true)}
        onMouseLeave={() => setHoverKaggleIMC(false)}
      />

      <ProjectCard
        onClick={() => navigate("/GitGram")}
        title="GitGram: GitHub - Telegram Notification Bot"
        description={PROJECT_SUMMARIES.gitGram}
        hover={hoverGitGram}
        onMouseEnter={() => setHoverGitGram(true)}
        onMouseLeave={() => setHoverGitGram(false)}
      />

      <ProjectCard
        onClick={() => navigate("/HeartbleedDemo")}
        title="Heartbleed Attack Simulation (CVE-2014-0160)"
        description={PROJECT_SUMMARIES.heartbleed}
        hover={hoverHeartbleedDemo}
        onMouseEnter={() => setHoverHeartbleedDemo(true)}
        onMouseLeave={() => setHoverHeartbleedDemo(false)}
      />

      <ProjectCard
        onClick={() => navigate("/SimpleReactFlowProject")}
        title="Simple ReactFlow Project"
        description={PROJECT_SUMMARIES.simpleReactFlow}
        hover={hoverSimpleReactFlowProject}
        onMouseEnter={() => setHoverSimpleReactFlowProject(true)}
        onMouseLeave={() => setHoverSimpleReactFlowProject(false)}
      />

      <ProjectCard
        onClick={() => navigate("/LoginRegTemplate")}
        title="Login/Registration Template"
        description={PROJECT_SUMMARIES.loginReg}
        hover={hoverLoginRegTemplate}
        onMouseEnter={() => setHoverLoginRegTemplate(true)}
        onMouseLeave={() => setHoverLoginRegTemplate(false)}
      />

      <ProjectCard
        onClick={() => navigate("/PlanetEDA")}
        title="Planetary Exploratory data analysis (EDA)"
        description={PROJECT_SUMMARIES.planetEDA}
        hover={hoverPlanetEDA}
        onMouseEnter={() => setHoverPlanetEDA(true)}
        onMouseLeave={() => setHoverPlanetEDA(false)}
      />

      <ProjectCard
        onClick={() => navigate("/OnTrackSG")}
        title="OnTrack SG App"
        description={PROJECT_SUMMARIES.onTrackSG}
        hover={hoverOnTrackSG}
        onMouseEnter={() => setHoverOnTrackSG(true)}
        onMouseLeave={() => setHoverOnTrackSG(false)}
      />

      </Reveal>
    )
  }


function DeepLearningMLDAEEE2025Hackathon() {
  const navigate = useNavigate();
  const [hoverBackProjects, setHoverBackProjects] = useState(false);
  const [hoverViewinGitDeepLearning, setHoverViewinGitDeepLearning] = useState(false); 
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="fade-in" style={styles.projectPage}>
      {/* Top bar: buttons LEFT, title centered, spacer RIGHT */}
      <div 
        style={{
          ...styles.projectHeader,
          ...(isMobile ? styles.projectHeaderMobile : {})
        }}
      >
        {/* BUTTONS FIRST (LEFT SIDE) */}
        <div 
       style={{
          ...styles.projectHeaderActions,
          ...(isMobile ? styles.projectHeaderActionsMobile : {})
        }}>
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
            style={{
              ...styles.topIconBtn,
              ...(isMobile ? styles.topIconBtnMobile : {}),
              opacity: hoverBackProjects ? 0.6 : 1,
              transform: hoverBackProjects ? "translateY(-2px) scale(1.05)" : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverBackProjects(true)}
            onMouseLeave={() => setHoverBackProjects(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/return.svg`}
              alt="Go back"
              style={styles.topIconImg}
            />
          </button>

          <button
            onClick={() =>
              window.open(
                "https://github.com/ArunKarthick19/Projects/tree/main/Deep%20Learning%202025"
              )
            }
            style={{
              ...styles.topGitBtn,
              opacity: hoverViewinGitDeepLearning ? 0.6 : 1,
              transform: hoverViewinGitDeepLearning
                ? "translateY(-2px) scale(1.03)"
                : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverViewinGitDeepLearning(true)}
            onMouseLeave={() => setHoverViewinGitDeepLearning(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
              alt="GitHub"
              style={styles.GitIcon_Projects}
            />
            View in GitHub
            <img
              src={`${process.env.PUBLIC_URL}/topRight.svg`}
              alt="Open"
              style={styles.TopRight}
            />
          </button>
        </div>

        {/* TITLE IN THE MIDDLE */}
        <h1
          style={{
            ...styles.DeepLearningHeading,
            ...(isMobile ? styles.DeepLearningHeadingMobile: {}),
          }}
        >
          DeepLearning MLDA@EEE 2025 Hackathon
        </h1>

        {/* SPACER ON THE RIGHT */}
        <div style={styles.projectHeaderSpacer} />
      </div>

      {/* Content in normal flow (no absolute positioning) */}
      <Reveal as="div" style={isMobile ? styles.projectContentMobile : styles.projectContent} stagger={0.18} y={30} once start="top bottom">
        <div style={
          isMobile ? styles.videoWrapMobile : styles.videoWrap}>
          <video
            style={styles.projectVideo}
            controls
            preload="metadata"
            onLoadedMetadata={() => requestAnimationFrame(() => ScrollTrigger.refresh())}
          >
            <source src="/DeepLearning.mp4" type="video/mp4" />
          </video>
        </div>

        <p style={styles.projectDescription}>
          Developed a comprehensive web-based management system to optimize Bosch's
          tool calibration processes, replacing manual worksheet-based tracking with
          an automated digital solution. The platform features an intuitive React
          frontend and Flask backend that enables real-time monitoring of calibration
          schedules, automated alerts for overdue equipment, and intelligent workload
          distribution among technicians. Key functionalities include advanced search
          and filtering capabilities, visual dashboards for tracking calibration dates,
          risk alert systems for tool faults, and a heuristic model that optimally
          redistributes workloads based on calibrator availability and due dates.
          This solution significantly reduces equipment downtime risk, ensures
          regulatory compliance, improves operational efficiency, and scales
          effectively as the tool database expands through features like batch
          processing and automated notifications.
        </p>
      </Reveal>
    </div>
  );
}

function OnTrackSGApp() {
  const navigate = useNavigate();
   const [hoverBackProjects, setHoverBackProjects] = useState(false);
   const [hoverViewinGitOnTrackSG, setHoverViewinGitOnTrackSG] = useState(false);
   const [isMobile, setIsMobile] = useState(false);
   
     useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  return (
    <div 
    className="fade-in"
    style={styles.container}>
      <h1 
      style={{
        ...styles.OnTrackSgHeading,
        ...(isMobile ? styles.OnTrackSgHeadingMobile : {}),
      }}
      >
        
        OnTrack SG App</h1>

       
  
      <button
        onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
        style={{
          ...styles.GoBackIconDeepLearning,
          ...(isMobile ? styles.GoBackProjectsMobileDeepLearning : {}),
          
          opacity: hoverBackProjects ? 0.6 : 1,
          transform: hoverBackProjects ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={() => setHoverBackProjects(true)}
        onMouseLeave={() => setHoverBackProjects(false)}
      >
        <img
          src={`${process.env.PUBLIC_URL}/return.svg`}
          alt="Go back"
          style={styles.GoBackIcon}
        />
      </button>

      <button
        onClick={() => window.open('https://github.com/ArunKarthick19/Projects/tree/main/OnTrack_SG_App')}
        style={{
          ...styles.ViewInGitOnTrackSG,
          ...(isMobile ? styles.ViewInGitMobileOnTrackSG : {}),

          opacity: hoverViewinGitOnTrackSG ? 0.6 : 1,
          transform: hoverViewinGitOnTrackSG ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
          transition: 'all 0.3s ease',
        }}
        onMouseEnter={() => setHoverViewinGitOnTrackSG(true)}
        onMouseLeave={() => setHoverViewinGitOnTrackSG(false)}
      >
        <img
          src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
          alt="Go back"
          style={styles.GitIcon_Projects}
        />
        View in GitHub
        <img
          src={`${process.env.PUBLIC_URL}/topRight.svg`}
          alt="Go back"
          style={styles.TopRight}
        />
      </button>

      <Reveal as="div" y={0} duration={1}>
        <video style={styles.videoOnTrackSG} controls width="100%" height="auto">
          <source src="/OnTrackSG.mp4" type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      </Reveal>

      <Reveal as="h2" y={0} duration={1} delay={0.15} style={styles.OnTrackSGProjectDescription}>OnTrack SG is a comprehensive mobile application designed to enhance the
         commuting experience for anyone traveling on Singapore’s
        MRT and LRT networks. The app features an interactive map that allows users to tap on stations for detailed,
        real-time information, including live train timings, crowd level indicators, and service alerts.
         With dedicated support for Bukit Panjang and Punggol LRT lines,
         OnTrack SG ensures that users can easily access information for all major transit routes.
         The intuitive interface, built with React Native, provides a seamless and visually engaging experience,
         while backend integration delivers up-to-date data for every station.
         Whether you’re planning your daily commute or exploring new
          parts of the city, OnTrack SG helps you navigate Singapore’s public transport system with confidence and ease.</Reveal>


    </div>
  );
}


function SimpleReactFlowProject() {
  const navigate = useNavigate();
  const [hoverBackProjects, setHoverBackProjects] = useState(false);
  const [hoverViewinGitDeepLearning, setHoverViewinGitDeepLearning] = useState(false); 
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="fade-in" style={styles.projectPage}>
      {/* Top bar: buttons LEFT, title centered, spacer RIGHT */}
      <div 
        style={{
          ...styles.projectHeader,
          ...(isMobile ? styles.projectHeaderMobile : {})
        }}
      >
        {/* BUTTONS FIRST (LEFT SIDE) */}
        <div 
       style={{
          ...styles.projectHeaderActions,
          ...(isMobile ? styles.projectHeaderActionsMobile : {})
        }}>
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
            style={{
              ...styles.topIconBtn,
              ...(isMobile ? styles.topIconBtnMobile : {}),
              opacity: hoverBackProjects ? 0.6 : 1,
              transform: hoverBackProjects ? "translateY(-2px) scale(1.05)" : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverBackProjects(true)}
            onMouseLeave={() => setHoverBackProjects(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/return.svg`}
              alt="Go back"
              style={styles.topIconImg}
            />
          </button>

          <button
            onClick={() =>
              window.open(
                "https://github.com/ArunKarthick19/Projects/tree/main/ReactFlow%20Project"
              )
            }
            style={{
              ...styles.topGitBtn,
              opacity: hoverViewinGitDeepLearning ? 0.6 : 1,
              transform: hoverViewinGitDeepLearning
                ? "translateY(-2px) scale(1.03)"
                : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverViewinGitDeepLearning(true)}
            onMouseLeave={() => setHoverViewinGitDeepLearning(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
              alt="GitHub"
              style={styles.GitIcon_Projects}
            />
            View in GitHub
            <img
              src={`${process.env.PUBLIC_URL}/topRight.svg`}
              alt="Open"
              style={styles.TopRight}
            />
          </button>
        </div>

        {/* TITLE IN THE MIDDLE */}
        <h1
          style={{
            ...styles.DeepLearningHeading,
            ...(isMobile ? styles.DeepLearningHeadingMobile: {}),
          }}
        >
          Simple ReactFlow Project
        </h1>

        {/* SPACER ON THE RIGHT */}
        <div style={styles.projectHeaderSpacer} />
      </div>

      {/* Content in normal flow (no absolute positioning) */}
      <Reveal as="div" style={isMobile ? styles.projectContentMobile : styles.projectContent} stagger={0.18} y={30} once start="top bottom">
        <div style={
          isMobile ? styles.videoWrapMobile : styles.videoWrap}>
          <video
            style={styles.projectVideo}
            controls
            preload="metadata"
            onLoadedMetadata={() => requestAnimationFrame(() => ScrollTrigger.refresh())}
          >
            <source src="/ReactFlow.mp4" type="video/mp4" />
          </video>
        </div>

        <p style={styles.projectDescription}>
This project is a dynamic flowchart creation tool built with React and ReactFlow that allows users to design 
custom workflow diagrams through an intuitive drag-and-drop interface. 
The application features four distinct node types - Start, Action, If/Else, and End nodes - each with custom styling and 
connection handles. Users can click on edges to insert new nodes, create complex branching logic with conditional If/Else 
nodes that automatically generate multiple pathways, and edit node properties through interactive forms. The tool includes 
advanced features like branch management for conditional nodes, intelligent edge routing, and node deletion with automatic 
reconnection logic to maintain workflow integrity. With its clean interface, minimap navigation, and real-time editing 
capabilities, this project demonstrates proficiency in React state management, custom component design, and creating 
sophisticated user interactions for technical workflow visualization.
        </p>
      </Reveal>
    </div>
  );
}

  function PlanetEDA() {
    const navigate = useNavigate();
    const [hoverBackProjects, setHoverBackProjects] = useState(false);
    const [hoverViewinGitPlanetEDA, setHoverViewinGitPlanetEDA] = useState(false);
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      checkMobile();
      window.addEventListener('resize', checkMobile);

      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
      <div>
        <div className="fade-in" style={styles.container}>
          <h1
            style={{
              ...styles.PlanetEDAHeading,
              ...(isMobile ? styles.Planetedaheadermobile : {}),
            }}
          >
          Planetary Exploratory data analysis (EDA)
          </h1>

          {/* Back Button */}
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
            style={{
              ...styles.GoBackIconDeepLearning,
              ...(isMobile ? styles.GoBackProjectsMobileDeepLearning : {}),
              opacity: hoverBackProjects ? 0.6 : 1,
              transform: hoverBackProjects
                ? 'translateY(-3px) scale(1.05)'
                : 'translateY(0) scale(1)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={() => setHoverBackProjects(true)}
            onMouseLeave={() => setHoverBackProjects(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/return.svg`}
              alt="Go back"
              style={styles.GoBackIcon}
            />
          </button>

          {/* GitHub Button */}
          <button
            onClick={() =>
              window.open(
                'https://github.com/ArunKarthick19/Projects/blob/main/PlanataryEDAYr1/Project/Final.ipynb'
              ) 
            }
            style={{
              ...styles.ViewInGitOnTrackSG,
              ...(isMobile ? styles.ViewInGitMobileOnTrackSG : {}),
              opacity: hoverViewinGitPlanetEDA ? 0.6 : 1,
              transform: hoverViewinGitPlanetEDA
                ? 'translateY(-3px) scale(1.05)'
                : 'translateY(0) scale(1)',
              transition: 'all 0.3s ease',
            }}
            onMouseEnter={() => setHoverViewinGitPlanetEDA(true)}
            onMouseLeave={() => setHoverViewinGitPlanetEDA(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
              alt="GitHub"
              style={styles.GitIcon_Projects}
            />
            View in GitHub
            <img
              src={`${process.env.PUBLIC_URL}/topRight.svg`}
              alt="Open link"
              style={styles.TopRight}
            />
          </button>

          {/* Project Description */}
          <Reveal
            as="h2"
            y={0}
            duration={1}
            style={{
            ...styles.PlanetEDADescription,
      top: isMobile ? '15vh' : '12vh',
      marginTop: isMobile ? '10px' : '1px',
      fontSize: isMobile ? 'clamp(13px, 3vw, 14px)' : 'clamp(16px, 2.5vw, 15px)',
            }}
          >
            PlanetEDA is a data-driven exploration of exoplanets beyond our solar
            system, combining astronomy with data science to uncover insights
            about planetary habitability. Using Python libraries such as Pandas,
            NumPy, Matplotlib, and Seaborn, this project analyzes a dataset of 572
            exoplanets to study their physical and orbital characteristics. It
            applies statistical and machine learning techniques including Linear,
            Polynomial, Decision Tree, and Random Forest Regression, to predict
            each planet’s Earth Similarity Index (ESI), which measures how
            Earth-like a planet is. PlanetEDA demonstrates how computational
            modeling can help identify potentially habitable worlds and serves as
            a step forward in using data analytics to answer one of humanity’s
            most fascinating questions: Are we alone in the universe?
          </Reveal>

          {/* PDF Viewer */}
          <Reveal as="div" delay={0.2}>
          <iframe
            src={`${process.env.PUBLIC_URL}/PlanetEDA.pdf`}
            title="PlanetEDA"
              style={{
      ...styles.PlanetEDApdfViewer,
      marginTop: isMobile ? '53vh' : '20vh', // More space on mobile
      marginLeft: isMobile ? '20px' : '50px', // Less margin on mobile
      width: isMobile ? '90%' : '90%',
    }}
          />
          </Reveal>
        </div>
      </div>
    );
  }


function LoginRegTemplate() {
  const navigate = useNavigate();
  const [hoverBackProjects, setHoverBackProjects] = useState(false);
  const [hoverViewinGitDeepLearning, setHoverViewinGitDeepLearning] = useState(false); 
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="fade-in" style={styles.projectPage}>
      {/* Top bar: buttons LEFT, title centered, spacer RIGHT */}
      <div 
        style={{
          ...styles.projectHeader,
          ...(isMobile ? styles.projectHeaderMobile : {})
        }}
      >
        {/* BUTTONS FIRST (LEFT SIDE) */}
        <div 
       style={{
          ...styles.projectHeaderActions,
          ...(isMobile ? styles.projectHeaderActionsMobile : {})
        }}>
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
            style={{
              ...styles.topIconBtn,
              ...(isMobile ? styles.topIconBtnMobile : {}),
              opacity: hoverBackProjects ? 0.6 : 1,
              transform: hoverBackProjects ? "translateY(-2px) scale(1.05)" : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverBackProjects(true)}
            onMouseLeave={() => setHoverBackProjects(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/return.svg`}
              alt="Go back"
              style={styles.topIconImg}
            />
          </button>

          <button
            onClick={() =>
              window.open(
                "https://github.com/ArunKarthick19/Projects/tree/main/Login%20Register%20boilerplate%20template"
              )
            }
            style={{
              ...styles.topGitBtn,
              opacity: hoverViewinGitDeepLearning ? 0.6 : 1,
              transform: hoverViewinGitDeepLearning
                ? "translateY(-2px) scale(1.03)"
                : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverViewinGitDeepLearning(true)}
            onMouseLeave={() => setHoverViewinGitDeepLearning(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
              alt="GitHub"
              style={styles.GitIcon_Projects}
            />
            View in GitHub
            <img
              src={`${process.env.PUBLIC_URL}/topRight.svg`}
              alt="Open"
              style={styles.TopRight}
            />
          </button>
        </div>

        {/* TITLE IN THE MIDDLE */}
        <h1
          style={{
            ...styles.DeepLearningHeading,
            ...(isMobile ? styles.DeepLearningHeadingMobile: {}),
          }}
        >
           Login/Registration boilerplate Template
        </h1>

        {/* SPACER ON THE RIGHT */}
        <div style={styles.projectHeaderSpacer} />
      </div>

      {/* Content in normal flow (no absolute positioning) */}
      <Reveal as="div" style={isMobile ? styles.projectContentMobile : styles.projectContent} stagger={0.18} y={30} once start="top bottom">
        <div style={
          isMobile ? styles.videoWrapMobile : styles.videoWrap}>
          <video
            style={styles.projectVideo}
            controls
            preload="metadata"
            onLoadedMetadata={() => requestAnimationFrame(() => ScrollTrigger.refresh())}
          >
            <source src="/logreg.mp4" type="video/mp4" />
          </video>
        </div>

        <p style={styles.projectDescription}>
A full-stack authentication boilerplate designed to jumpstart projects that require secure user login,
registration, and session management. Built with a Node.js / Express backend, Next.js frontend, and MongoDB,
this template implements industry-standard practices such as JWT-based authentication, bcrypt password hashing,
and RESTful APIs. It includes a modern, responsive UI styled with CSS, making it a solid foundation for SaaS platforms, dashboards, and production-ready
web and phone applications.
        </p>
      </Reveal>
    </div>
  );
}

function HeartbleedDemo() {
  const navigate = useNavigate();
  const [hoverBackProjects, setHoverBackProjects] = useState(false);
  const [hoverViewinGitDeepLearning, setHoverViewinGitDeepLearning] = useState(false); 
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="fade-in" style={styles.projectPage}>
      {/* Top bar: buttons LEFT, title centered, spacer RIGHT */}
      <div 
        style={{
          ...styles.projectHeader,
          ...(isMobile ? styles.projectHeaderMobile : {})
        }}
      >
        {/* BUTTONS FIRST (LEFT SIDE) */}
        <div 
       style={{
          ...styles.projectHeaderActions,
          ...(isMobile ? styles.projectHeaderActionsMobile : {})
        }}>
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
            style={{
              ...styles.topIconBtn,
              ...(isMobile ? styles.topIconBtnMobile : {}),
              opacity: hoverBackProjects ? 0.6 : 1,
              transform: hoverBackProjects ? "translateY(-2px) scale(1.05)" : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverBackProjects(true)}
            onMouseLeave={() => setHoverBackProjects(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/return.svg`}
              alt="Go back"
              style={styles.topIconImg}
            />
          </button>

          <button
            onClick={() =>
              window.open(
                "https://github.com/ArunKarthick19/Projects/tree/main/heartbleed-demo"
              )
            }
            style={{
              ...styles.topGitBtn,
              opacity: hoverViewinGitDeepLearning ? 0.6 : 1,
              transform: hoverViewinGitDeepLearning
                ? "translateY(-2px) scale(1.03)"
                : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverViewinGitDeepLearning(true)}
            onMouseLeave={() => setHoverViewinGitDeepLearning(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
              alt="GitHub"
              style={styles.GitIcon_Projects}
            />
            View in GitHub
            <img
              src={`${process.env.PUBLIC_URL}/topRight.svg`}
              alt="Open"
              style={styles.TopRight}
            />
          </button>
        </div>

        {/* TITLE IN THE MIDDLE */}
        <h1
          style={{
            ...styles.DeepLearningHeading,
            ...(isMobile ? styles.DeepLearningHeadingMobile: {}),
          }}
        >
           Heartbleed Attack Simulation (CVE-2014-0160)
        </h1>

        {/* SPACER ON THE RIGHT */}
        <div style={styles.projectHeaderSpacer} />
      </div>

      {/* Content in normal flow (no absolute positioning) */}
      <Reveal as="div" style={isMobile ? styles.projectContentMobile : styles.projectContent} stagger={0.18} y={30} once start="top bottom">
        <div style={
          isMobile ? styles.videoWrapMobile : styles.videoWrap}>
          <video
            style={styles.projectVideo}
            controls
            preload="metadata"
            onLoadedMetadata={() => requestAnimationFrame(() => ScrollTrigger.refresh())}
          >
            <source src="/heartbleeddemo.mp4" type="video/mp4" />
          </video>
        </div>

        <p style={styles.projectDescription}>
A 3-layer interactive demo of the Heartbleed vulnerability, built to illustrate how the infamous 2014 OpenSSL bug worked from first principles all the way to a real exploit chain.
Layer 1 strips the bug down to its core — a toy TCP heartbeat server that demonstrates the claimed_len vs actual_len memory over-read mechanic, no TLS needed. Layer 2 fires the actual CVE-2014-0160 exploit against a real OpenSSL 1.0.1f instance running in a Docker container. Layer 3 puts it all together in a full attack chain: a simulated banking app where stolen session tokens from server memory are used to hijack an admin account and perform fraudulent actions.
The project runs entirely locally via Docker, and supports both vulnerable and patched modes — so you can see exactly what the fix does. Built with Python (Flask), PHP, and raw TLS socket manipulation.
        </p>
      </Reveal>
    </div>
  );
}

function TrueFrameHackathon() {
  const navigate = useNavigate();
  const [hoverBackProjects, setHoverBackProjects] = useState(false);
  const [hoverViewinGit, setHoverViewinGit] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const slides = [
    { type: 'image', src: `${process.env.PUBLIC_URL}/Pic1_todds.png`, alt: 'TrueFrame slide 1' },
    { type: 'image', src: `${process.env.PUBLIC_URL}/Pic2_todds.png`, alt: 'TrueFrame slide 2' },
    { type: 'image', src: `${process.env.PUBLIC_URL}/pic3_todds.png`, alt: 'TrueFrame slide 3' },
    { type: 'pdf', src: encodeURI(`${process.env.PUBLIC_URL}/TODDS TECHJAM.pdf`), title: 'TrueFrame report' },
  ];

  return (
    <div className="fade-in" style={styles.projectPage}>
      {/* Top bar: buttons LEFT, title centered, spacer RIGHT */}
      <div
        style={{
          ...styles.projectHeader,
          ...(isMobile ? styles.projectHeaderMobile : {})
        }}
      >
        {/* BUTTONS FIRST (LEFT SIDE) */}
        <div
       style={{
          ...styles.projectHeaderActions,
          ...(isMobile ? styles.projectHeaderActionsMobile : {})
        }}>
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
            style={{
              ...styles.topIconBtn,
              ...(isMobile ? styles.topIconBtnMobile : {}),
              opacity: hoverBackProjects ? 0.6 : 1,
              transform: hoverBackProjects ? "translateY(-2px) scale(1.05)" : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverBackProjects(true)}
            onMouseLeave={() => setHoverBackProjects(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/return.svg`}
              alt="Go back"
              style={styles.topIconImg}
            />
          </button>

          <button
            onClick={() =>
              window.open("https://github.com/ArunKarthick19/Projects/tree/main/TikTok-TechJam")
            }
            style={{
              ...styles.topGitBtn,
              opacity: hoverViewinGit ? 0.6 : 1,
              transform: hoverViewinGit
                ? "translateY(-2px) scale(1.03)"
                : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverViewinGit(true)}
            onMouseLeave={() => setHoverViewinGit(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
              alt="GitHub"
              style={styles.GitIcon_Projects}
            />
            View in GitHub
            <img
              src={`${process.env.PUBLIC_URL}/topRight.svg`}
              alt="Open"
              style={styles.TopRight}
            />
          </button>
        </div>

        {/* TITLE IN THE MIDDLE */}
        <h1
          style={{
            ...styles.DeepLearningHeading,
            ...(isMobile ? styles.DeepLearningHeadingMobile: {}),
          }}
        >
          TrueFrame — Robust Detection of AI-Generated Content
        </h1>

        {/* SPACER ON THE RIGHT */}
        <div style={styles.projectHeaderSpacer} />
      </div>

      {/* Content in normal flow (no absolute positioning) */}
      <Reveal as="div" style={isMobile ? styles.projectContentMobile : styles.projectContent} stagger={0.18} y={30} once start="top bottom">
        <p style={styles.projectKicker}>TikTok TechJam 2026 — Track 5</p>

        <ProjectCarousel slides={slides} />

        <p style={styles.projectDescription}>
Built with a team of two other groupmates, <a href="https://github.com/wjw55" target="_blank" rel="noreferrer" style={styles.inlineLink}>Jiawei Wang</a> and <a href="https://github.com/Derekqua" target="_blank" rel="noreferrer" style={styles.inlineLink}>Derek Qua</a>, TrueFrame detects AI-generated images in a way that holds up under real-world conditions like compression, resizing, and cropping. We landed on a CLIP backbone fused with frequency-domain (FFT) features — since generative artifacts often surface in frequency space rather than raw pixels — after testing and ruling out alternatives like PRNU noise-residual branches, DIRE-style diffusion-reconstruction-error detection, and multi-scale FFT variants.
        </p>
        <p style={styles.projectDescription}>
The final architecture used gated probability fusion between CLIP and FFT features, arrived at by comparing it head-to-head against a DINOv2-based variant and isolating which technique actually drove the improvement. Evaluated across a 12-condition robustness suite, the model achieved a 0.98 combined score — built in roughly 72 hours as part of a 2,000+ team competition.
        </p>

        <p style={styles.projectDescription}>
          <a
            href="https://devpost.com/software/trueframe-robust-ai-image-detection#updates"
            target="_blank"
            rel="noreferrer"
            style={styles.inlineLink}
          >
            View this project on Devpost →
          </a>
        </p>
      </Reveal>
    </div>
  );
}

function KaggleIMC() {
  const navigate = useNavigate();
  const [hoverBackProjects, setHoverBackProjects] = useState(false);
  const [hoverViewinGitDeepLearning, setHoverViewinGitDeepLearning] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="fade-in" style={styles.projectPage}>
      {/* Top bar: buttons LEFT, title centered, spacer RIGHT */}
      <div
        style={{
          ...styles.projectHeader,
          ...(isMobile ? styles.projectHeaderMobile : {})
        }}
      >
        {/* BUTTONS FIRST (LEFT SIDE) */}
        <div
       style={{
          ...styles.projectHeaderActions,
          ...(isMobile ? styles.projectHeaderActionsMobile : {})
        }}>
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
            style={{
              ...styles.topIconBtn,
              ...(isMobile ? styles.topIconBtnMobile : {}),
              opacity: hoverBackProjects ? 0.6 : 1,
              transform: hoverBackProjects ? "translateY(-2px) scale(1.05)" : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverBackProjects(true)}
            onMouseLeave={() => setHoverBackProjects(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/return.svg`}
              alt="Go back"
              style={styles.topIconImg}
            />
          </button>

          <button
            onClick={() =>
              window.open(
                "https://github.com/ArunKarthick19/Projects/tree/main/Kaggle%20Image%20Matching%20Challenge%20(IMC)"
              )
            }
            style={{
              ...styles.topGitBtn,
              opacity: hoverViewinGitDeepLearning ? 0.6 : 1,
              transform: hoverViewinGitDeepLearning
                ? "translateY(-2px) scale(1.03)"
                : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverViewinGitDeepLearning(true)}
            onMouseLeave={() => setHoverViewinGitDeepLearning(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/github-142-svgrepo-com.svg`}
              alt="GitHub"
              style={styles.GitIcon_Projects}
            />
            View in GitHub
            <img
              src={`${process.env.PUBLIC_URL}/topRight.svg`}
              alt="Open"
              style={styles.TopRight}
            />
          </button>
        </div>

        {/* TITLE IN THE MIDDLE */}
        <h1
          style={{
            ...styles.DeepLearningHeading,
            ...(isMobile ? styles.DeepLearningHeadingMobile: {}),
          }}
        >
          Kaggle Image Matching Challenge (IMC)
        </h1>

        {/* SPACER ON THE RIGHT */}
        <div style={styles.projectHeaderSpacer} />
      </div>

      {/* Content in normal flow (no absolute positioning) */}
      <Reveal as="div" style={isMobile ? styles.projectContentMobile : styles.projectContent} stagger={0.18} y={30} once start="top bottom">
        <p style={styles.projectDescription}>
The Image Matching Challenge tackles a core problem in 3D computer vision: reconstructing accurate 3D scenes from unordered collections of 2D images, often captured under varying lighting, viewpoints, and occlusions. Working as part of a team, I built a full image-matching and reconstruction pipeline that combines modern deep learning features with classical geometry. The system uses DINOv2 for global image embeddings, and SuperPoint and ALIKED for local keypoint detection, paired with LightGlue for robust feature matching across image pairs. Matches are filtered using RANSAC to reject outliers before being passed to COLMAP, which performs structure-from-motion to recover camera poses and a sparse 3D point cloud. The project involved balancing matching accuracy against computational cost across large image sets, and understanding where each stage of the pipeline was prone to failure.
        </p>

        <div style={isMobile ? styles.pdfWrapMobile : styles.pdfWrap}>
          <iframe
            src={encodeURI(`${process.env.PUBLIC_URL}/Kaggle Image Matching Challenge (IMC).pdf`)}
            title="Kaggle Image Matching Challenge (IMC)"
            style={styles.projectPdf}
            onLoad={() => requestAnimationFrame(() => ScrollTrigger.refresh())}
          />
        </div>
      </Reveal>
    </div>
  );
}

// GitGram architecture, drawn inline so it follows the light/dark theme.
// Blue = notification flow (1-3), orange = command flow (A-C).
const GG_BLUE = '#3b82f6';
const GG_ORANGE = '#f97316';
const GG_RED = '#ef4444';
// Text tokens: darker than INK_MUTED in light mode, unchanged in dark.
const GG_TEXT = 'var(--diagram-text)';
const GG_BLUE_TEXT = 'var(--diagram-blue-text)';
const GG_ORANGE_TEXT = 'var(--diagram-orange-text)';

function GitGramArchitecture() {
  // Unique marker ids, since the lightbox renders a second copy of the SVG.
  const uid = useId().replace(/:/g, '');
  const box =(x, y, w, h, stroke, fill) => (
    <rect x={x} y={y} width={w} height={h} rx="10" style={{ fill, stroke, strokeWidth: 1.5 }} />
  );
  const label = (x, y, text, { size = 11.5, weight = 400, color = GG_TEXT, mono = false } = {}) => (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      style={{
        fill: color,
        fontSize: size,
        fontWeight: weight,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace' : 'inherit',
      }}
    >
      {text}
    </text>
  );
  const arrow = (x1, y1, x2, y2, color, marker, dashed = false) => (
    <line
      x1={x1} y1={y1} x2={x2} y2={y2}
      markerEnd={`url(#${marker}-${uid})`}
      style={{ stroke: color, strokeWidth: 2, strokeDasharray: dashed ? '6 5' : 'none' }}
    />
  );
  const badge = (cx, cy, text, color) => (
    <g>
      <circle cx={cx} cy={cy} r="10" style={{ fill: color }} />
      <text x={cx} y={cy + 4} textAnchor="middle" style={{ fill: '#fff', fontSize: 11, fontWeight: 700 }}>
        {text}
      </text>
    </g>
  );
  const legendRow = { margin: '4px 0', color: GG_TEXT, fontSize: 'clamp(11px, 1.2vw, 13px)' };
  const legendHead = (color) => ({ margin: '0 0 6px', color, fontWeight: 700, fontSize: 'clamp(12px, 1.3vw, 14px)' });

  return (
    <div>
      <svg viewBox="0 0 560 430" width="100%" role="img" aria-label="GitGram architecture: GitHub and Telegram talk to two Vercel serverless functions, which share state in Upstash Redis">
        <defs>
          {[['gg-arrow-blue', GG_BLUE], ['gg-arrow-orange', GG_ORANGE]].map(([id, color]) => (
            <marker key={id} id={`${id}-${uid}`} viewBox="0 0 10 10" refX="10" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M0,0 L10,5 L0,10 z" style={{ fill: color }} />
            </marker>
          ))}
        </defs>

        {/* Vercel container */}
        <rect x="195" y="10" width="180" height="410" rx="14" style={{ fill: INK_TINT, stroke: HAIRLINE, strokeWidth: 1.5, strokeDasharray: '6 5' }} />
        {label(285, 30, 'Vercel · serverless (Node.js)', { size: 11 })}

        {/* Nodes */}
        {box(5, 45, 140, 90, INK_MUTED, INK_TINT)}
        {label(75, 80, 'GitHub', { size: 15, weight: 700, color: INK })}
        {label(75, 100, 'Repositories')}
        {label(75, 116, 'Webhooks + REST API')}

        {box(5, 300, 140, 90, INK_MUTED, INK_TINT)}
        {label(75, 335, 'Telegram', { size: 15, weight: 700, color: INK })}
        {label(75, 355, 'Groups & DMs')}
        {label(75, 371, 'Bot API')}

        {box(205, 45, 160, 90, GG_BLUE, 'rgba(59, 130, 246, 0.08)')}
        {label(285, 80, '/api/webhook', { size: 14, weight: 700, color: INK, mono: true })}
        {label(285, 100, 'validate · filter events')}
        {label(285, 116, 'fan out to groups')}

        {box(205, 300, 160, 90, GG_ORANGE, 'rgba(249, 115, 22, 0.08)')}
        {label(285, 335, '/api/bot', { size: 14, weight: 700, color: INK, mono: true })}
        {label(285, 355, 'commands · DM setup')}
        {label(285, 371, 'group lifecycle')}

        {box(415, 165, 140, 100, GG_RED, 'rgba(239, 68, 68, 0.08)')}
        {label(485, 192, 'Upstash Redis', { size: 15, weight: 700, color: INK })}
        {label(485, 214, 'repo → groups')}
        {label(485, 230, 'group → repos')}
        {label(485, 246, 'setup sessions')}

        {/* Notification flow */}
        {arrow(145, 90, 205, 90, GG_BLUE, 'gg-arrow-blue')}
        {arrow(365, 115, 415, 185, GG_BLUE, 'gg-arrow-blue')}
        {arrow(225, 135, 130, 300, GG_BLUE, 'gg-arrow-blue')}

        {/* Command flow */}
        {arrow(145, 345, 205, 345, GG_ORANGE, 'gg-arrow-orange')}
        {arrow(365, 320, 415, 245, GG_ORANGE, 'gg-arrow-orange')}
        {arrow(225, 300, 110, 135, GG_ORANGE, 'gg-arrow-orange', true)}

        {badge(175, 90, '1', GG_BLUE)}
        {badge(390, 150, '2', GG_BLUE)}
        {badge(196, 185, '3', GG_BLUE)}
        {badge(175, 345, 'A', GG_ORANGE)}
        {badge(390, 282, 'B', GG_ORANGE)}
        {badge(190, 250, 'C', GG_ORANGE)}
      </svg>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 24px', marginTop: '14px', borderTop: `1px solid ${HAIRLINE}`, paddingTop: '12px' }}>
        <div style={{ flex: '1 1 200px' }}>
          <p style={legendHead(GG_BLUE_TEXT)}>Notification flow</p>
          <p style={legendRow}>1 · GitHub POSTs a push / PR / issue event</p>
          <p style={legendRow}>2 · Look up which groups follow that repo</p>
          <p style={legendRow}>3 · Send a formatted message to each group</p>
        </div>
        <div style={{ flex: '1 1 200px' }}>
          <p style={legendHead(GG_ORANGE_TEXT)}>Command flow</p>
          <p style={legendRow}>A · User runs /addrepo, /status, /admin …</p>
          <p style={legendRow}>B · Save the group ↔ repo mapping</p>
          <p style={legendRow}>C · Register the repo webhook (once, idempotent)</p>
        </div>
      </div>
    </div>
  );
}

function GitGram() {
  const navigate = useNavigate();
  const [hoverBackProjects, setHoverBackProjects] = useState(false);
  const [diagramOpen, setDiagramOpen] = useState(false);
  const [hoverDiagramClose, setHoverDiagramClose] = useState(false);

  useEffect(() => {
    if (!diagramOpen) return;
    const onKeyDown = (e) => {
      if (e.key === 'Escape') setDiagramOpen(false);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [diagramOpen]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  return (
    <div className="fade-in" style={styles.projectPage}>
      {/* Top bar: buttons LEFT, title centered, spacer RIGHT */}
      <div
        style={{
          ...styles.projectHeader,
          ...(isMobile ? styles.projectHeaderMobile : {})
        }}
      >
        {/* BUTTONS FIRST (LEFT SIDE) */}
        <div
       style={{
          ...styles.projectHeaderActions,
          ...(isMobile ? styles.projectHeaderActionsMobile : {})
        }}>
          <button
            onClick={() => navigate('/', { state: { scrollTo: 'projects' } })}
            style={{
              ...styles.topIconBtn,
              ...(isMobile ? styles.topIconBtnMobile : {}),
              opacity: hoverBackProjects ? 0.6 : 1,
              transform: hoverBackProjects ? "translateY(-2px) scale(1.05)" : "none",
              transition: "all 0.25s ease",
            }}
            onMouseEnter={() => setHoverBackProjects(true)}
            onMouseLeave={() => setHoverBackProjects(false)}
          >
            <img
              src={`${process.env.PUBLIC_URL}/return.svg`}
              alt="Go back"
              style={styles.topIconImg}
            />
          </button>

        </div>

        {/* TITLE IN THE MIDDLE */}
        <h1
          style={{
            ...styles.DeepLearningHeading,
            ...(isMobile ? styles.DeepLearningHeadingMobile: {}),
          }}
        >
          GitGram: GitHub - Telegram Notification Bot
        </h1>

        {/* SPACER ON THE RIGHT */}
        <div style={styles.projectHeaderSpacer} />
      </div>

      {/* Content in normal flow (no absolute positioning) */}
      <Reveal as="div" style={isMobile ? styles.projectContentMobile : styles.projectContent} stagger={0.18} y={30} once start="top bottom">
        <p style={styles.projectStack}>
          Node.js · Vercel serverless functions · Upstash Redis · GitHub Webhooks &amp; REST API · Telegram Bot API
        </p>

        {/* Demo video and architecture diagram side by side (stacked on mobile) */}
        <div style={isMobile ? styles.mediaRowMobile : styles.mediaRow}>
          <video
            style={{ ...styles.mediaItem, flexGrow: 1.4 }}
            controls
            preload="metadata"
            onLoadedMetadata={() => requestAnimationFrame(() => ScrollTrigger.refresh())}
          >
            <source src={`${process.env.PUBLIC_URL}/Gitgramdemo.mp4`} />
          </video>
          <div
            role="button"
            tabIndex={0}
            aria-label="Open the architecture diagram full size"
            onClick={() => setDiagramOpen(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDiagramOpen(true); } }}
            style={{ ...styles.mediaItem, cursor: 'zoom-in' }}
          >
            <GitGramArchitecture />
          </div>
        </div>

        {diagramOpen && createPortal(
          <div
            className="fade-in"
            style={styles.lightboxOverlay}
            onClick={() => setDiagramOpen(false)}
          >
            <button
              aria-label="Close full size view"
              onClick={() => setDiagramOpen(false)}
              onMouseEnter={() => setHoverDiagramClose(true)}
              onMouseLeave={() => setHoverDiagramClose(false)}
              style={{
                ...styles.lightboxCloseBtn,
                backgroundColor: hoverDiagramClose ? "rgba(255,255,255,0.25)" : "rgba(255,255,255,0.1)",
                transform: hoverDiagramClose ? "scale(1.1)" : "scale(1)",
              }}
            >
              ×
            </button>
            <div style={styles.lightboxDiagram} onClick={(e) => e.stopPropagation()}>
              <GitGramArchitecture />
            </div>
          </div>,
          document.body
        )}

        <h2 style={styles.projectSubheading}>Why I built it</h2>
        <p style={styles.projectDescription}>
My team coordinated on Telegram, but repo activity lived on GitHub. Nobody watched GitHub notifications, so pushes and PRs went unnoticed until someone asked about them in chat. I wanted repo activity to show up where the team was already talking.
        </p>

        <h2 style={styles.projectSubheading}>What it does</h2>
        <p style={styles.projectDescription}>
GitGram sends GitHub events (pushes, pull requests, issues) to Telegram groups as clean, readable messages. The mapping is many-to-many: one group can follow several repos, and one repo can notify several groups. Setup happens through bot commands like /addrepo, /status, /collaborators and /admin, with the sensitive steps moved into DMs so nothing leaks into the group chat.
        </p>


        <h2 style={styles.projectSubheading}>How it works</h2>
        <p style={styles.projectDescription}>
The bot runs on two serverless endpoints, with no server to keep alive. <strong>/api/webhook</strong> receives events from GitHub, validates and filters them, looks up which groups follow that repo in Redis, and sends a message to each one. <strong>/api/bot</strong> handles Telegram commands. It runs the setup flows, stores group↔repo mappings in Redis, and registers the GitHub webhook through the REST API.
        </p>
        <p style={styles.projectDescription}>
Redis holds both directions of the mapping (repo → groups, group → repos) plus short-lived setup sessions. That keeps every lookup O(1) and every function stateless.
        </p>

        <h2 style={styles.projectSubheading}>Design decisions</h2>
        <p style={styles.projectDescription}>
<strong>Stateless functions, state in Redis.</strong> Each endpoint does one job and keeps nothing in memory between requests. All state lives in Upstash Redis, so the bot scales to zero when idle and handles bursts of events without any servers to manage.
        </p>
        <p style={styles.projectDescription}>
<strong>Two-way mapping.</strong> Redis stores both repo → groups and group → repos. When an event arrives, finding every group to notify is a single lookup. Commands like /status are just as cheap in the other direction.
        </p>
        <p style={styles.projectDescription}>
<strong>Idempotent webhook registration.</strong> One GitHub hook per repo, however many groups follow it. The bot checks before creating a hook, and quietly recreates one if it was deleted on GitHub, so the links can't drift out of sync.
        </p>
        <p style={styles.projectDescription}>
<strong>Signal over noise.</strong> Only the events people act on get forwarded: real pushes, plus PRs and issues being opened, closed or reopened. Labels, edits and empty branch pushes are dropped before they reach the chat.
        </p>
        <p style={styles.projectDescription}>
<strong>Private setup in DMs.</strong> Setup steps that involve credentials run in a private chat with the bot, so nothing sensitive is posted in the group.
        </p>
      </Reveal>
    </div>
  );
}


  function Others() {
    const navigate = useNavigate();
    const [hoverDiscord, setHoverDiscord] = useState(false);
    const [hoverStrava, setHoverStrava] = useState(false);
    const [hoverBackProjects, setHoverBackProjects] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    

      useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };
      
      checkMobile();
      window.addEventListener('resize', checkMobile);
      
      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
      <div className="fade-in link-container" style={{
        ...styles.LinkPage,
      }}>


        <button
          onClick={() => navigate('/')}
          style={{
            ...styles.GoBack,
            ...(isMobile ? styles.GoBackProjectsMobile : {}),
            opacity: hoverBackProjects ? 0.6 : 1,
            transform: hoverBackProjects ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverBackProjects(true)}
          onMouseLeave={() => setHoverBackProjects(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/return.svg`}
            alt="Go back"
            style={styles.GoBackIcon}
          />
        </button>

        
            <Reveal
          as="button"
          y={30}
          onClick={() => window.open('https://discord.com/users/carbon_atom2395')}
          style={{
            ...styles.discord,
            opacity: hoverDiscord ? 0.6 : 1,
            transform: hoverDiscord ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverDiscord(true)}
          onMouseLeave={() => setHoverDiscord(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/discord.svg`}
            alt="Discord"
            style={styles.discordIcon}
          />
          Discord
        </Reveal>


        <Reveal
          as="button"
          y={30}
          delay={0.12}
          onClick={() => window.open('https://www.strava.com/athletes/96076943')}
          style={{
            ...styles.strava,
            opacity: hoverStrava ? 0.6 : 1,
            transform: hoverStrava ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverStrava(true)}
          onMouseLeave={() => setHoverStrava(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/strava.svg`}
            alt="Strava"
            style={styles.stravaIcon}
          />
          Strava
        </Reveal>
      </div>
    );
  }

  function ContactMe() {
    const [isMobile, setIsMobile] = useState(false);
    const [hoverTele, setHoverTele] = useState(false);
    const [hoverBackProjects, setHoverBackProjects] = useState(false);
    const [hoverEmail, setHoverEmail] = useState(false);
    const navigate = useNavigate();

    useEffect(() => {
      const checkMobile = () => {
        setIsMobile(window.innerWidth <= 768);
      };

      checkMobile();
      window.addEventListener('resize', checkMobile);
      

      return () => window.removeEventListener('resize', checkMobile);
    }, []);

    return (
      <div className="fade-in link-container" style={{
        ...styles.LinkPage,
      }}>
        <button
          onClick={() => navigate('/')}
          style={{
            ...styles.GoBack,
            ...(isMobile ? styles.GoBackProjectsMobile : {}),
            opacity: hoverBackProjects ? 0.6 : 1,
            transform: hoverBackProjects ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverBackProjects(true)}
          onMouseLeave={() => setHoverBackProjects(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/return.svg`}
            alt="Go back"
            style={styles.GoBackIcon}
          />
        </button>
          
      
          
          <Reveal
          as="button"
          y={30}
          onClick={() => window.open('https://t.me/arunk1903')}
          style={{
            ...styles.tele,
            opacity: hoverTele ? 0.6 : 1,
            transform: hoverTele ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverTele(true)}
          onMouseLeave={() => setHoverTele(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/tele.svg`}
            alt="Telegram"
            style={styles.teleIcon}
          />
          Telegram: @arunk1903
        </Reveal>

        <Reveal
          as="button"
          y={30}
          delay={0.12}
            onClick={() => window.open('mailto:arunkarthick192003@gmail.com')}
          style={{
            ...styles.email,
            opacity: hoverEmail ? 0.6 : 1,
            transform: hoverEmail ? 'translateY(-3px) scale(1.05)' : 'translateY(0) scale(1)',
            transition: 'all 0.3s ease',
          }}
          onMouseEnter={() => setHoverEmail(true)}
          onMouseLeave={() => setHoverEmail(false)}
        >
          <img
            src={`${process.env.PUBLIC_URL}/gmail.svg`}
            alt="Email"
            style={styles.discordIcon}
          />
          Email: arunkarthick192003@gmail.com
        </Reveal>

      
      </div>
    );
  }

  //routes
  // Crossfades between routes. Deliberately opacity-only (no translateY) —
  // this wraps every page including the home page's `position: fixed` nav,
  // and animating `transform` on an ancestor of a fixed element permanently
  // turns it into that element's containing block (breaks the fixed nav).
  // Each page's own content still gets its own fade+rise via <Reveal>.
  function AnimatedRoutes() {
    const location = useLocation();
    const [displayLocation, setDisplayLocation] = useState(location);
    const wrapperRef = useRef(null);
    const hasMounted = useRef(false);
    const hasFadedIn = useRef(false);

    useLayoutEffect(() => {
      const el = wrapperRef.current;
      if (!el) return;

      if (!hasMounted.current) {
        hasMounted.current = true;
        gsap.killTweensOf(el);
        gsap.fromTo(el, { opacity: 0 }, { opacity: 1, duration: 0.4, ease: 'power2.out' });
        return;
      }

      if (location.pathname === displayLocation.pathname) return;

      gsap.killTweensOf(el);
      gsap.to(el, {
        opacity: 0,
        duration: 0.2,
        ease: 'power2.in',
        onComplete: () => {
          // A client-side route change keeps whatever scroll position the
          // previous page was at — reset it here so every new page opens
          // at its own top. Pages that want a specific scroll spot instead
          // (like the homepage's "?section=projects" deep link) still win,
          // since their own scroll effect runs after this on mount.
          window.scrollTo(0, 0);
          setDisplayLocation(location);
        },
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    useLayoutEffect(() => {
      const el = wrapperRef.current;
      if (!el) return;
      if (!hasFadedIn.current) {
        hasFadedIn.current = true;
        return;
      }
      gsap.killTweensOf(el);
      gsap.to(el, { opacity: 1, duration: 0.3, ease: 'power2.out' });
    }, [displayLocation]);

    return (
      <div ref={wrapperRef}>
        <Routes location={displayLocation}>
          <Route path="/" element={<App />} />
          <Route path="/links" element={<Links />} />
          <Route path="/resume" element={<Resume />} />
          <Route path="/projects" element={<Navigate to="/?section=projects" replace />} />
          <Route path="/DeepLearning" element={<DeepLearningMLDAEEE2025Hackathon />} />
          <Route path="/TrueFrame" element={<TrueFrameHackathon />} />
          <Route path="/OnTrackSG" element={<OnTrackSGApp />} />
          <Route path="/SimpleReactFlowProject" element={<SimpleReactFlowProject />} />
          <Route path="/PlanetEDA" element={<PlanetEDA/>} />
          <Route path="/LoginRegTemplate" element={<LoginRegTemplate/>} />
          <Route path="/HeartbleedDemo" element={<HeartbleedDemo/>} />
          <Route path="/KaggleIMC" element={<KaggleIMC/>} />
          <Route path="/GitGram" element={<GitGram/>} />

          {/* Old nested project URLs — kept working in case they were shared anywhere. */}
          <Route path="/projects/DeepLearning" element={<Navigate to="/DeepLearning" replace />} />
          <Route path="/projects/OnTrackSG" element={<Navigate to="/OnTrackSG" replace />} />
          <Route path="/projects/SimpleReactFlowProject" element={<Navigate to="/SimpleReactFlowProject" replace />} />
          <Route path="/projects/PlanetEDA" element={<Navigate to="/PlanetEDA" replace />} />
          <Route path="/projects/LoginRegTemplate" element={<Navigate to="/LoginRegTemplate" replace />} />
          <Route path="/projects/HeartbleedDemo" element={<Navigate to="/HeartbleedDemo" replace />} />
          <Route path="/projects/KaggleIMC" element={<Navigate to="/KaggleIMC" replace />} />

          <Route path="/others" element={<Others />} />
          <Route path="/contactme" element={<ContactMe />} />
        </Routes>
      </div>
    );
  }

  // Router wrapper at top level
  export default function AppWrapper() {
    const themeState = useThemeState();

    return (
      <ThemeContext.Provider value={themeState}>
        <Router>
          <AnimatedRoutes />
          <ThemeToggle />
          <Analytics />
          <SpeedInsights />
        </Router>
      </ThemeContext.Provider>
    );
  }

  const styles = {
    themeToggle: {
      position: 'fixed',
      top: '18px',
      right: '20px',
      zIndex: 1100,
      width: '40px',
      height: '40px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 0,
      borderRadius: '50%',
      border: `1px solid ${HAIRLINE}`,
      backgroundColor: 'var(--paper-translucent)',
      backdropFilter: 'blur(8px)',
      color: INK,
      cursor: 'pointer',
      transition: 'all 0.25s ease',
    },

  navBar: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      padding: "1rem 2rem",
      background: "#333",
      color: "white",
    },
    container: {
      textAlign: 'center',
      backgroundColor: PAPER,
      minHeight: '100vh',
      // Top gap is padding, not margin, so it's filled with the page colour
      // instead of showing the body behind it as a strip.
      padding: 'calc(5vh + 20px) 20px 20px',
    },
  heading: {
    //paddingTop: '20vh',
    position: 'relative',
    zIndex: 1,
    textAlign: 'left',
    paddingLeft: '20px',
    color: INK,
    fontSize: 'clamp(32px, 5vw, 45px)',
    fontFamily: "'Fraunces', Georgia, serif",
    fontWeight: '600',
    marginBottom: '10px',

  },

  heroSection: {
    position: 'relative',
    minHeight: '100vh',
    width: '100%',
  },

  scrollHint: {
    position: 'absolute',
    bottom: '50px',
    left: '50%',
    transform: 'translateX(-50%)',
    zIndex: 2,
    background: 'none',
    border: 'none',
    padding: '8px',
    color: INK,
  },

  scrollHintArrow: {
    display: 'inline-block',
    fontSize: '22px',
  },

  scrollMoreHint: {
    textAlign: 'center',
    color: INK,
    fontSize: '14px',
    fontWeight: '500',
    marginTop: '24px',
  },

  scrollMoreHintArrow: {
    display: 'inline-block',
    fontSize: '14px',
  },

  homeSection: {
    position: 'relative',
    width: '100%',
    padding: '50px 20px 80px',
    scrollMarginTop: '80px',
  },

  homeSectionTitle: {
    color: INK,
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: 'clamp(26px, 4vw, 42px)',
    fontWeight: '600',
    textAlign: 'center',
    marginBottom: '10px',
  },

  projectsGitHubLinkWrap: {
    textAlign: 'center',
  },

  projectsGitHubLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    color: INK,
    fontSize: '14px',
    fontWeight: '600',
    textDecoration: 'none',
    padding: '8px 18px',
    borderRadius: '999px',
    border: `2px solid ${'black'}`,
    backgroundColor: 'transparent',
    opacity: 0.85,
  },

  skillsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
    gap: '20px',
    width: '100%',
    maxWidth: '1100px',
    margin: '20px auto 0',
  },

  skillCard: {
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: 'transparent',
    border: `1px solid ${HAIRLINE}`,
    borderRadius: '2px',
    padding: '20px 22px',
    minWidth: 0,
  },

  skillCategoryTitle: {
    color: INK,
    fontSize: '14px',
    fontWeight: '700',
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '12px',
  },

  skillChipRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '8px',
  },

  skillChip: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '6px',
    maxWidth: '100%',
    boxSizing: 'border-box',
    color: INK,
    fontSize: '13px',
    fontWeight: '500',
    padding: '6px 12px',
    borderRadius: '999px',
    border: '1px solid var(--chip-border)',
    backgroundColor: 'transparent',
    whiteSpace: 'normal',
    overflowWrap: 'break-word',
    cursor: 'default',
    transition: 'border-color 0.2s ease',
  },

  skillChipIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    boxSizing: 'border-box',
    width: '16px',
    height: '16px',
    borderRadius: '50%',
    backgroundColor: 'white',
    padding: '2px',
    flexShrink: 0,
  },

  skillChipIcon: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
  },

  hero: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    textAlign: 'center',
    padding: '0 20px',
    position: 'relative',
    zIndex: 1,
    transform: 'translateY(-17vh)',
  },



  sheading: {
    marginTop: '5px',
    position: 'relative',
    zIndex: 1,
    textAlign: 'left',
    //paddingLeft: '30px',
    color: INK,
    fontSize: 'clamp(14px, 2vw, 20px)',
    fontFamily: 'Arial, sans-serif',
    fontWeight: 'normal',
  },

  imageCaption: {
    position: 'absolute',
    bottom: '20px', 
    left: '20px', 
    zIndex: 1,
    color: '#E0E0E0',
    fontSize: 'clamp(11px, 1.5vw, 14px)',
    fontFamily: 'Georgia, serif', 
    fontStyle: 'italic',
    fontWeight: '300',
    textShadow: '1px 1px 3px rgba(0, 0, 0, 0.9)',
    margin: 0,
    padding: '8px 12px',
    backgroundColor: 'rgba(0, 0, 0, 0.3)', 
    borderRadius: '4px',
    backdropFilter: 'blur(5px)',
  },

    
    navbar: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: 'transparent',
      backdropFilter: 'blur(10px)',
      padding: '15px',
      zIndex: 1000,
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },

    hamburgerButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      padding: '10px',
      zIndex: 1002,
    },

    hamburgerIcon: {
      display: 'flex',
      flexDirection: 'column',
      gap: '5px',
      width: '24px',
      height: '24px',
      justifyContent: 'center',
    },

    hamburgerLine: {
      width: '24px',
      height: '2px',
      backgroundColor: INK,
      transition: 'all 0.3s ease',
    },

    mobileMenu: {
      position: 'fixed',
      top: 0,
      left: 0,
      height: '100vh',
      width: '250px',
      backgroundColor: 'var(--paper-solid-overlay)',
      borderRight: `1px solid ${HAIRLINE}`,
      backdropFilter: 'blur(10px)',
      padding: '80px 20px 20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '20px',
      transition: 'transform 0.3s ease',
      zIndex: 1001,
    },

    // mobileNavButton: {
    //   backgroundColor: '#fefbd8',
    //   color: 'black',
    //   padding: '15px 20px',
    //   borderRadius: '25px',
    //   border: 'none',
    //   fontSize: '16px',
    //   fontWeight: '600',
    //   cursor: 'pointer',
    //   transition: 'all 0.3s ease',
    //   width: '100%',
    // },

    MobileButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    height: '50px',
    backgroundColor: 'transparent',
    color: INK,
    border: 'transparent',
    //border: '2px solid white',
    borderRadius: '25px',
    padding: '8px 20px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: 'Arial, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

      MobileLinkButton:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '10vh',
      left: '20px',
      width: '150px',
      height: '40px',
      backgroundColor: 'transparent',
      color: 'white',
      border: '2px solid white',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },


      MobileResumeButton:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '18vh',
      left: '20px',
      width: '200px',
      height: '40px',
      backgroundColor: 'transparent',
      color: 'white',
      border: '2px solid white',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

        MobileProjectsButton:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '26vh',
      left: '20px',
      width: '200px',
      height: '40px',
      backgroundColor: 'transparent',
      color: 'white',
      border: '2px solid white',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

    navButtons: {
      display: 'flex',
      justifyContent: 'center',
      gap: '20px',
      minWidth: 'min-content',
      padding: '0 20px',
    },

    // navButton: {
    //   backgroundColor: '#fefbd8',
    //   color: 'black',
    //   padding: '10px 20px',
    //   borderRadius: '25px',
    //   border: 'none',
    //   fontSize: '16px',
    //   fontWeight: '600',
    //   cursor: 'pointer',
    //   transition: 'all 0.3s ease',
    //   whiteSpace: 'nowrap',
    // },

  LinkButton: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'absolute',
    top: '30px',
    left: '20px',
    width: '115px',
    height: '20px',
    backgroundColor: 'transparent',
    color: 'white',
    border: 'none', // ✅ completely removes border
    outline: 'none', // ✅ removes focus outline
    borderRadius: '25px',
    fontSize: '16px',
    fontWeight: '600',
    fontFamily: 'Arial, sans-serif',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },

    logoIcon: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '6px',
      width: '40px',
      height: '40px',
      
    },

      logoIconMobile: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '70px',
      width: '40px',
      height: '40px',
      
    },

    ResumeButton:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '20px',
      width: '140px',
      height: '20px',
      backgroundColor: 'transparent',
      color: INK,
      border:'transparent',
      // border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

    ProjectsButton:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '170px',
      width: '160px',
      height: '20px',
      backgroundColor: 'transparent',
      color: INK,
      border:'transparent',
      //border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

      OthersButton:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '30px',
      left: '460px',
      width: '65px',
      height: '20px',
      backgroundColor: 'transparent',
      color: 'white',
      border:'transparent',
      //border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    
    },

      ContactmeButton:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '340px',
      width: '100px',
      height: '20px',
      backgroundColor: 'transparent',
      color: 'white',
      border:'transparent',
      //border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textShadow: '0 2px 6px rgba(0,0,0,0.45)',
    
    },

  LinkedinNew:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '415px',
      width: '30px',
      height: '20px',
      backgroundColor: 'transparent',
      color: 'white',
      border:'transparent',
      //border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textShadow: '0 2px 6px rgba(0,0,0,0.45)',
    },

    gmailnew:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '375px',
      width: '30px',
      height: '20px',
      backgroundColor: 'transparent',
      color: 'white',
      border:'transparent',
      //border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textShadow: '0 2px 6px rgba(0,0,0,0.45)',
    
    },

      githubnew:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '335px',
      width: '30px',
      height: '20px',
      backgroundColor: 'transparent',
      color: 'white',
      border:'transparent',
      //border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textShadow: '0 2px 6px rgba(0,0,0,0.45)',
    },

    
      telenew:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '452px',
      width: '25px',
      height: '20px',
      backgroundColor: 'transparent',
      color: 'white',
      border:'transparent',
      //border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textShadow: '0 2px 6px rgba(0,0,0,0.45)',
    },

        outlook:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '485px',
      width: '30px',
      height: '20px',
      backgroundColor: 'transparent',
      color: 'white',
      border:'transparent',
      //border: '2px solid white',
      borderRadius: '25px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      textShadow: '0 2px 6px rgba(0,0,0,0.45)',
    },





    linkedin: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      width: 'auto',
      minWidth: '240px',
      height: '60px',
      backgroundColor: '#0077B5', 
      color: 'white',
      borderRadius: '30px',
      border: 'none',
      padding: '12px 25px',
      fontSize: '20px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

    strava: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      width: 'auto',
      minWidth: '240px',
      height: '60px',
      backgroundColor: '#FC4C02',
      color: 'white',
      borderRadius: '30px',
      border: 'none',
      padding: '12px 25px',
      fontSize: '20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

      tele: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      width: 'auto',
      minWidth: '240px',
      height: '60px',
      backgroundColor: '#0088cc',
      color: 'white',
      borderRadius: '30px',
      border: 'none',
      padding: '12px 25px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    discord: {
      backgroundColor: '#5865F2',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      width: 'auto',
      minWidth: '240px',
      height: '60px',
      color: 'white',
      borderRadius: '30px',
      border: 'none',
      padding: '12px 25px',
      fontSize: '20px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

      email: {
      backgroundColor: '#f0f0f0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      width: 'auto',
      minWidth: '240px',
      height: '60px',
      color: '#333333',  
      borderRadius: '30px',
      border: 'none',
      padding: '12px 25px',
      fontSize: '15px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    linkedinIcon: {
      width: '70px',
      height: '70px',
      marginRight: '3px',
      filter: 'brightness(0) invert(1)', 
    },

  stravaIcon: {
      width: '30px',     
      height: '30px',     
      marginRight: '3px',
      filter: 'brightness(0) invert(1)',
    },

    teleIcon: {
      width: '50px',     
      height: '50px',     
      marginRight: '3px',
      // filter: 'brightness(0) invert(1)',
    },

    discordIcon: {
      width: '50px',
      height: '50px',
      marginRight: '8px',
      //filter: 'brightness(0) invert(1)', //why?????
    },

    LinkPage: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '20px',
      minHeight: '100vh',
      backgroundColor: PAPER,
      padding: '20px',
    },

    LinkPageMobile: {
      flexDirection: 'column',
      gap: '30px',
    },

    git: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '15px',
      width: 'auto',
      minWidth: '240px',
      height: '60px',
      backgroundColor: '#24292e', 
      color: 'white',
      borderRadius: '30px',
      border: 'none',
      padding: '12px 25px',
      fontSize: '20px',
      fontWeight: '700', 
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

    GitIcon: {
      width: '40px',
      height: '40px',
      marginRight: '3px',
      filter: 'brightness(0) invert(1)',
    },

      LinkedinNewIcon: {
      width: '25px',
      height: '25px',
      marginRight: '3px',
      filter: 'brightness(0) invert(1)',
    },

        Noinvericon: {
      width: '25px',
      height: '25px',
      marginRight: '3px',
      //filter: 'brightness(0) invert(1)',
    },
      GitIcon_Projects: {
      width: '16px',
      height: '16px',
      marginRight: '2px',
      filter: 'brightness(0) invert(1)',
    },

    TopRight: {
      width: '14px',
      height: '14px',
      marginLeft: '4px',
      paddingleft:'8px',
      filter: 'brightness(0) invert(1)',
    },

    GoBack: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '20px',
      width: '50px',
      height: '40px',
      backgroundColor: 'transparent',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

    GoBackIconDeepLearning: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '-8px',
      left: '20px',
      width: '40px',
      height: '38px',
      backgroundColor: 'transparent',
      color: 'white',
      border: 'transparent',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

      ViewInGitOnTrackSG: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '-8px',
      left: '100px',
      width: 'auto',
      height: '40px',
      backgroundColor: '#24292e',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

    ViewInGitButtonMobile:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '15px',
      left: '130px',
      width: 'auto',
      height: '40px',
      backgroundColor: '#24292e',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

      ViewInGitButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '20px',
      left: '100px',
      width: 'auto',
      height: '40px',
      backgroundColor: '#24292e',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '550',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },


      ViewInGitButtonOnTrackSG: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '35px',
      left: '950px',
      width: 'auto',
      height: '40px',
      backgroundColor: '#24292e',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '550',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

    GoBackMobile: {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      marginBottom: '20px',
      width: 'auto',
      height:'45px',
      border: `1px solid ${INK}`,
    },

    GoBackProjectsMobile:{
          display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '2vh',
      left: '20px',
      width: 'auto',
      height: '45px',
      backgroundColor: 'transparent',
      color: INK,
      border: `1px solid ${INK}`,
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
    
    GoBackIcon: {
      width: '45px',
      height: '45px',
      marginRight: '3px',
      filter: ICON_FILTER,
    },


    resumeContainer: {
      backgroundColor: PAPER,
      minHeight: '100vh',
      padding: '20px',
      position: 'relative',
    },

      projectsContainer: {
      backgroundColor: 'black',
      minHeight: '100vh',
      padding: '20px',
      position: 'relative',
    },

    pdfContainer: {
      width: '100%',
      height: 'calc(100vh - 50px)',
      marginTop: '60px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    },

    pdfViewer: {
      width: '100%',
      height: '100%',
      border: 'none',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      backgroundColor: 'white',
    },

      PlanetEDApdfViewer: {
      marginTop:'35vh',
      marginLeft:'50px',
      width: '90%',
      height: '800px',
      border: 'none',
      borderRadius: '8px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
      backgroundColor: 'white',
    },

    downloadButton: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '3vh',
      left: '20vh',
      width: 'auto',
      height: '40px',
      backgroundColor: 'transparent',
      color: INK,
      border: `1px solid ${INK}`,
      borderRadius: '25px',
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

    downloadButtonMobile: {
      position: 'relative',
      top: 'auto',
      left: 'auto',
      marginBottom: '20px',
    },

    downloadIcon: {
      width: '20px',
      height: '20px',
      filter: ICON_FILTER,
    },








  ViewInGitOnTrackSGButtonMobile: {
    // Override the desktop positioning for mobile
    top: '65px', // Position it below the title
    left: '20px', // Align with other mobile elements
    width: 'auto',
  },

    projectListStack: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      maxWidth: '900px',
      margin: '0 auto',
      marginTop: '20px',
      paddingBottom: '80px',
      borderBottom: `1px solid ${HAIRLINE}`,
    },

    projectCard: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
      width: '100%',
      backgroundColor: 'transparent',
      color: INK,
      border: 'none',
      borderTop: `1px solid ${HAIRLINE}`,
      borderRadius: 0,
      padding: '22px 4px',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      textAlign: 'left',
      transition: 'background-color 0.25s ease',
    },

    projectCardTop: {
      position: 'relative',
      display: 'flex',
      alignItems: 'baseline',
    },

    projectCardTitle: {
      fontFamily: "'Fraunces', Georgia, serif",
      fontSize: '19px',
      fontWeight: '600',
      lineHeight: '1.35',
      borderBottom: '1px solid transparent',
      transition: 'border-color 0.25s ease',
    },

    projectCardDescription: {
      fontSize: '13.5px',
      fontWeight: '400',
      lineHeight: '1.5',
      color: INK_MUTED,
    },

    projectCardCta: {
      position: 'relative',
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: '13px',
      fontWeight: '600',
      letterSpacing: '0.02em',
      color: INK,
    },

    projectCardArrow: {
      display: 'inline-block',
      transition: 'transform 0.3s ease',
    },

    // DeepLearningHeading: {
    //   color: 'white',
    //   fontSize: 'clamp(20px, 4vw, 43px)',
    //   fontWeight: 'bold',
    //   marginBottom: '20px',
    // },  
      OnTrackSgHeading: {
      color: INK,
      fontFamily: "'Fraunces', Georgia, serif",
      fontSize: 'clamp(20px, 4vw, 43px)',
      fontWeight: 'bold',
      marginBottom: '20px',
    },
    

      PlanetEDAHeading: {
      color: INK,
      fontFamily: "'Fraunces', Georgia, serif",
      fontSize: 'clamp(20px, 4vw, 30px)',
      fontWeight: 'bold',
      marginBottom: '20px',
    },  



DeepLearningHeadingMobile: {
  fontSize: "clamp(18px, 5vw, 26px)",

  // ✅ THIS is what centers it in flex column
  alignSelf: "center",
  width: "100%",
  textAlign: "center",

  // allow wrap so it looks nicer on phone
  whiteSpace: "normal",
  maxWidth: "100%",
  margin: 0,
  
},

Planetedaheadermobile: {
  fontSize: "clamp(18px, 5vw, 26px)",

  // ✅ THIS is what centers it in flex column
  alignSelf: "center",
  width: "100%",
  textAlign: "center",

  // allow wrap so it looks nicer on phone
  whiteSpace: "normal",
  maxWidth: "100%",
  margin: 0,
  marginTop: "20px",
  
},


projectHeaderActionsMobile: {
  width: "100%",
  justifyContent: "flex-start",
},



  OnTrackSgHeadingMobile: {
    color: INK,
    fontSize: 'clamp(20px, 4vw, 35px)',
    fontWeight: 'bold',
    top: '35px',
    position: 'absolute',
    left: '50%',                    
    transform: 'translateX(-50%)',  
  },


    videoDeepLearning: {
      width: '60%',
      height: 'auto',
      borderRadius: '6px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0)',
      top: '15vh',
      left: '50%',
      transform: 'translateX(-50%)',
      position: 'absolute',
    },

    videoOnTrackSG: {
      width: '40%',
      height: '50%',
      borderRadius: '6px',
      boxShadow: '0 4px 8px rgba(0, 0, 0, 0)',
      top: '15vh',
      left: '50%',
      transform: 'translateX(-50%)',
      position: 'absolute',
    },
      GoBackProjectsMobileDeepLearning:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '-2vh',
      left: '20px',
      width: 'auto',
      height: '40px',
      backgroundColor: 'transparent',
      color: INK,
      border: `1px solid ${INK}`,
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },

      ViewInGitMobileOnTrackSG:{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      position: 'absolute',
      top: '-2vh',
      left: '120px',
      width: 'auto',
      height: '40px',
      backgroundColor: '#24292e',
      color: 'white',
      border: 'none',
      borderRadius: '25px',
      padding: '8px 20px',
      fontSize: '16px',
      fontWeight: '600',
      fontFamily: 'Arial, sans-serif',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
    },
  OnTrackSGProjectDescription: {
      position: 'absolute',
      top: '70vh', 
      left: '50%',
      transform: 'translateX(-50%)',
      color: INK,
      fontSize: 'clamp(14px, 2.5vw, 20px)',
      fontWeight: '300',
      maxWidth: '1200px', 
      width: '90%',
      textAlign: 'justify', 
      lineHeight: '1.4',
    
  },

  ReactFlowProjectDescription: {
      position: 'absolute',
      top: '85vh', 
      left: '50%',
      transform: 'translateX(-50%)',
      color: INK,
      fontSize: 'clamp(14px, 2.5vw, 20px)',
      fontWeight: '300',
      maxWidth: '1200px', 
      width: '90%',
      textAlign: 'justify', 
      lineHeight: '1.4',
      
      
  },

  PlanetEDADescription: {
    position: 'absolute', 
    top: '25vh', 
    left: '50%', 
    transform: 'translateX(-50%)',
    color: INK,
    fontSize: 'clamp(14px, 2.5vw, 15px)',
    fontWeight: '300',
    maxWidth: '1200px', 
    width: '90%', 
    textAlign: 'justify',
    lineHeight: '1.4',
    paddingLeft: '10px',
    paddingRight: '10px',
    zIndex: 10,
  },
  diffhomeContainer: {
    position: 'relative',
    textAlign: 'center', 
    margin: 0, // Changed from marginTop: '5vh'
    minHeight: '100vh',
    //maxHeight: '100vh', // Add this to prevent overflow, also comment to let scroll
    //overflow: 'hidden', // Add this to hide any overflow, also comment to let scroll
    padding: '0', // Changed from '10px'
    // Clips the zoomed-in Skills section so it can't widen the page.
    // 'clip' (not 'hidden') so this doesn't become a scroll container.
    overflowX: 'clip',
    backgroundColor: PAPER,
  },

      homeOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.4)', // Semi-transparent dark overlay
      zIndex: 0,
    },

    nextSection: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    color: "white",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    textAlign: "center",
    padding: "40px",
  },

  scrollDownButton: {
    marginTop: "-10px",
    padding: "10px 24px",
    borderRadius: "20px",
    border: "1px solid white",
    background: "transparent",
    color: "white",
    cursor: "pointer",
    position: "relative", // ✅ enables zIndex
    zIndex: 5,            // ✅ above overlay/nav
  },

  mobileSocialRow: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between', // or 'center'
    alignItems: 'center',
    gap: '12px',
    marginTop: '20px',
    width: '100%',
  },

  mobileIconButton: {
    width: '44px',
    height: '44px',
    borderRadius: '12px',
    border: '0px solid rgba(255,255,255,0.25)',
    backgroundColor: 'transparent',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 0,
    cursor: 'pointer',
  },

  mobileSocialIcon: {
    width: '22px',
    height: '22px',
    objectFit: 'contain',
  },

  mobileIconButtonHover: {
    opacity: 0.75,
    transform: 'translateY(-2px) scale(1.06)',
    border: '0px solid rgba(255,255,255,0.6)',
  },
  projectPage: {
    minHeight: "100vh",
    backgroundColor: PAPER,
    padding: "0",
  },

  projectHeader: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    display: "grid",
    gridTemplateColumns: "auto 1fr auto", // ✅ CHANGED: buttons, title, spacer
    alignItems: "center",
    padding: "15px 20px",
    minHeight: "80px",
    background: "var(--paper-translucent)",
    borderBottom: `1px solid ${HAIRLINE}`,
    backdropFilter: "blur(8px)",
  },

  projectHeaderMobile: {
  display: "flex",
  flexDirection: "column",
  alignItems: "stretch",
  gap: "10px",
  padding: "10px 12px",
  minHeight: "unset",
},

  projectHeaderActions: {
    justifySelf: "start", // ✅ Keeps buttons on the left
    display: "flex",
    gap: "10px",
    alignItems: "center",
    flexWrap: "wrap",
    paddingLeft: "20px",
    maxWidth: "320px", // caps the column so 3+ buttons wrap instead of overlapping the centered title
  },

  projectHeaderSpacer: {
     justifySelf: "end",
  width: "300px",   // minimal, but...
  },

  topIconBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "48px",
    height: "40px",
    backgroundColor: "transparent",
    border: "0px solid white",
    borderRadius: "22px",
    cursor: "pointer",
  },

    topIconBtnMobile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    width: "48px",
    height: "40px",
    backgroundColor: "transparent",
    border: `1px solid ${INK}`,
    borderRadius: "22px",
    cursor: "pointer",
  },

  topIconImg: {
    width: "35px",
    height: "35px",
    filter: ICON_FILTER,
  },

  topGitBtn: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "5px",
    height: "32px",
    padding: "6px 12px",
    backgroundColor: "#24292e",
    color: "white",
    border: "none",
    borderRadius: "20px",
    cursor: "pointer",
    fontSize: "13px",
    fontWeight: "600",
    fontFamily: "Arial, sans-serif",
  },
DeepLearningHeading: {
  color: INK,
  fontFamily: "'Fraunces', Georgia, serif",
  fontSize: "clamp(24px, 3.5vw, 36px)",
  fontWeight: "600",
  margin: 0,

  /* ✅ center within the middle grid column */
  justifySelf: "center",
  textAlign: "center",

  /* ✅ allow it to shrink without pushing layout */
  minWidth: 0,
  maxWidth: "100%",
  overflowWrap: "break-word",
  lineHeight: 1.25,

  pointerEvents: "none",
},



  videoWrap: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
  },

videoWrapMobile: {
  marginTop: "0px", // ✅ Remove extra margin
  width: "100%",
  display: "flex",
  justifyContent: "center",
},

  projectVideo: {
    width: "min(1000px, 95%)",
    height: "auto",
    display: "block",
  },

  pdfWrap: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    marginTop: "30px",
  },

  pdfWrapMobile: {
    width: "100%",
    display: "flex",
    justifyContent: "center",
    marginTop: "10px",
  },

  projectPdf: {
    width: "min(1000px, 95%)",
    height: "80vh",
    border: `1px solid ${HAIRLINE}`,
    borderRadius: "8px",
    backgroundColor: "white",
    display: "block",
  },

  projectKicker: {
    textAlign: 'center',
    color: INK_MUTED,
    fontSize: '13px',
    fontWeight: '700',
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    margin: '10px 0 0',
  },

  inlineLink: {
    color: 'inherit',
    textDecoration: 'underline',
    textDecorationColor: INK_FAINT,
  },

  carousel: {
    width: 'min(900px, 95%)',
    margin: '30px auto 0',
  },

  carouselStage: {
    position: 'relative',
    flex: 1,
    minWidth: 0,
    height: '65vh',
    borderRadius: '8px',
    overflow: 'hidden',
    backgroundColor: 'white',
    boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
  },

  carouselSlide: {
    position: 'absolute',
    inset: 0,
    transition: 'opacity 0.6s ease',
  },

  carouselPdf: {
    width: '100%',
    height: '100%',
    border: 'none',
    display: 'block',
  },

  carouselImage: {
    width: '100%',
    height: '100%',
    objectFit: 'contain',
    display: 'block',
    backgroundColor: 'white',
  },

  carouselDots: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginTop: '14px',
  },

  carouselRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },

  carouselNavBtn: {
    flexShrink: 0,
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'transparent',
    color: INK,
    fontSize: '24px',
    lineHeight: '40px',
    textAlign: 'center',
    padding: 0,
    cursor: 'pointer',
    transition: 'background-color 0.25s ease, transform 0.25s ease',
  },

  carouselDot: {
    width: '9px',
    height: '9px',
    borderRadius: '50%',
    border: 'none',
    cursor: 'pointer',
    padding: 0,
    transition: 'background-color 0.3s ease',
  },

  lightboxOverlay: {
    position: 'fixed',
    inset: 0,
    backgroundColor: 'rgba(0,0,0,0.9)',
    backdropFilter: 'blur(6px)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 3000,
    cursor: 'zoom-out',
  },

  lightboxImage: {
    maxWidth: '92vw',
    maxHeight: '92vh',
    objectFit: 'contain',
    borderRadius: '4px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    cursor: 'default',
  },

  // Paper panel so an inline (theme-coloured) diagram stays legible on the dark overlay.
  lightboxDiagram: {
    // Also capped by viewport height so the whole diagram (SVG is 560×430,
    // plus ~180px of legend and padding) fits on short laptop screens.
    width: 'min(1000px, 92vw, calc((92vh - 180px) * 1.3))',
    maxHeight: '92vh',
    overflowY: 'auto',
    boxSizing: 'border-box',
    padding: 'clamp(16px, 3vw, 32px)',
    backgroundColor: PAPER,
    borderRadius: '8px',
    boxShadow: '0 8px 32px rgba(0,0,0,0.5)',
    cursor: 'default',
  },

  lightboxCloseBtn: {
    position: 'fixed',
    top: '20px',
    right: '24px',
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    border: 'none',
    color: 'white',
    fontSize: '26px',
    lineHeight: '44px',
    textAlign: 'center',
    padding: 0,
    cursor: 'pointer',
    transition: 'background-color 0.25s ease, transform 0.25s ease',
  },

  projectDescription: {
    color: INK,
    margin: "30px auto",
    maxWidth: "1000px",
    width: "95%",
    lineHeight: 1.6,
    textAlign: "justify",
    fontSize: "clamp(13px, 1.7vw, 16px)",
    fontWeight: 301,
  },

  projectSubheading: {
    color: INK,
    margin: "40px auto 0",
    maxWidth: "1000px",
    width: "95%",
    fontSize: "clamp(17px, 2.2vw, 22px)",
    fontWeight: 600,
  },

  projectStack: {
    textAlign: 'center',
    color: INK_MUTED,
    fontSize: '14px',
    margin: '12px auto 0',
    width: '95%',
  },

  mediaRow: {
    width: "min(1200px, 95%)",
    margin: "30px auto 0",
    display: "flex",
    gap: "20px",
    alignItems: "center",
  },

  mediaRowMobile: {
    width: "100%",
    margin: "10px auto 0",
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },

  mediaItem: {
    flex: "1 1 0",
    minWidth: 0,
    width: "100%",
    height: "auto",
    display: "block",
    borderRadius: "8px",
  },

projectContent: {
  padding: "0 20px 40px",
  //marginTop: "70px", // ✅ Space for fixed header
},
projectContentMobile: {
  padding: "15px",
},



  };
  // Test comment for a new push